/**
 * Consolidated Blueprint Service
 * 
 * Combines all blueprint-related functionality:
 * - Generation (from blueprintGeneratorService.ts)
 * - Validation (from blueprintValidationService.ts)
 * - Locking (from blueprintLockService.ts)
 * - Sync (from blueprintPlanSyncService.ts)
 * - Tree (from blueprintTreeService.ts)
 * - Gap Analysis (from gapAnalysisService.ts)
 * - SDLC Population (from sdlcPopulationService.ts)
 * 
 * This service maintains 100% backward compatibility with existing services.
 */

import { supabase } from '../../lib/supabase';
import { AnthropicService } from '../ai/AnthropicService';
import { GitHubService } from '../github/GitHubService';
import { JiraService } from '../jira/JiraService';
import { PlanTreeService } from '../planTreeService';
import { RequirementsService } from '../requirementsService';
import { FullStackArchitectureService, type ArchitectureTree, type LayerNode, type GapAnalysisResult } from '../fullStackArchitectureService';
import { logger } from '../loggingService';
import type { Blueprint } from '../../types';
import type { AgentAnalysisResult } from '../agentBasedAnalysisService';
import React from 'react'; // Placeholder for imports - actual imports would be added

// Import the validator
import { validateAndSanitize, validateBlueprintContent, type ValidationResult } from '../../spec/validator';

// ============================================================================
// Type Definitions
// ============================================================================

export interface Requirements {
  projectName: string;
  description: string;
  features: string[];
  constraints?: string[];
  targetAudience?: string;
  timeline?: string;
  budget?: string;
  techStack?: {
    frontend?: string;
    backend?: string;
    database?: string;
    infrastructure?: string;
  };
}

export interface ValidationResult {
  isValid: boolean;
  score: number;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  suggestions: string[];
  aiReview?: {
    summary: string;
    strengths: string[];
    weaknesses: string[];
    improvements: string[];
  };
}

export interface ValidationError {
  field: string;
  message: string;
  severity: 'critical' | 'error';
}

export interface ValidationWarning {
  field: string;
  message: string;
  severity: 'warning' | 'info';
}

export interface BlueprintLockStatus {
  isLocked: boolean;
  lockedAt?: Date;
  lockedBy?: string;
  reason?: string;
  canModify: boolean;
}

export interface BlueprintModificationAttempt {
  blueprintId: string;
  userId: string;
  attemptedAt: Date;
  modificationType: 'edit' | 'delete' | 'unlock';
  denied: boolean;
  reason: string;
}

export interface BlueprintTreeNode {
  id: string;
  type: 'root' | 'section' | 'item' | 'subitem';
  label: string;
  icon: string;
  children?: BlueprintTreeNode[];
  data?: any;
  count?: number;
  status?: 'complete' | 'in_progress' | 'pending' | 'needs_review';
  lastUpdated?: string;
  promptContext?: string;
}

export interface BlueprintTreeData {
  root: BlueprintTreeNode;
  sections: {
    requirements: BlueprintTreeNode;
    architecture: BlueprintTreeNode;
    conventions: BlueprintTreeNode;
    security: BlueprintTreeNode;
    design: BlueprintTreeNode;
    phases: BlueprintTreeNode;
  };
}

export interface GapAnalysisReport {
  projectId: string;
  analyzedAt: string;
  gaps: GapAnalysisResult[];
  summary: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
    byType: Record<string, number>;
  };
  integrationScore: number; // 0-100
  recommendations: string[];
}

// SDLC Phase Mapping
const PHASE_MAPPING: Record<string, {
  planTreePhaseId: string;
  blueprintSectionId: string;
  steps: Array<{ id: string; name: string }>;
}> = {
  requirements: {
    planTreePhaseId: 'requirements',
    blueprintSectionId: 'scope',
    steps: [
      { id: 'scope', name: 'Scope Definition' },
      { id: 'features', name: 'Features & Requirements' },
      { id: 'user-stories', name: 'User Stories' },
      { id: 'stakeholders', name: 'Stakeholder Analysis' },
    ],
  },
  design: {
    planTreePhaseId: 'design',
    blueprintSectionId: 'architecture',
    steps: [
      { id: 'architecture', name: 'Architecture Design' },
      { id: 'tech-stack', name: 'Technology Stack Selection' },
      { id: 'schema', name: 'Database Schema Design' },
      { id: 'api-design', name: 'API Design' },
      { id: 'wireframes', name: 'Wireframes & Mockups' },
      { id: 'theme', name: 'Theme & Branding' },
    ],
  },
  development: {
    planTreePhaseId: 'development',
    blueprintSectionId: 'standards',
    steps: [
      { id: 'coding-standards', name: 'Coding Standards & Conventions' },
      { id: 'feature-dev', name: 'Feature Development' },
      { id: 'unit-testing', name: 'Unit Testing' },
      { id: 'integration-testing', name: 'Integration Testing' },
    ],
  },
  testing: {
    planTreePhaseId: 'testing',
    blueprintSectionId: 'standards',
    steps: [
      { id: 'unit-testing', name: 'Unit Testing' },
      { id: 'integration-testing', name: 'Integration Testing' },
      { id: 'security-testing', name: 'Security Testing' },
      { id: 'performance-testing', name: 'Performance Testing' },
      { id: 'user-acceptance-testing', name: 'User Acceptance Testing (UAT)' },
    ],
  },
  deployment: {
    planTreePhaseId: 'deployment',
    blueprintSectionId: 'timeline',
    steps: [
      { id: 'ci-cd', name: 'CI/CD Pipeline Setup' },
      { id: 'monitoring', name: 'Monitoring & Logging' },
      { id: 'release-management', name: 'Release Management' },
    ],
  },
  maintenance: {
    planTreePhaseId: 'maintenance',
    blueprintSectionId: 'timeline',
    steps: [
      { id: 'bug-fixing', name: 'Bug Fixing & Support' },
      { id: 'enhancements', name: 'Feature Enhancements' },
      { id: 'documentation-updates', name: 'Documentation Updates' },
    ],
  },
};

// ============================================================================
// Consolidated Blueprint Service
// ============================================================================

export class BlueprintService {
  /**
   * Generate blueprint from GitHub repository analysis
   */
  static async generateFromGitHub(
    projectId: string,
    userId: string
  ): Promise<Blueprint | null> {
    try {
      // Check for agent analysis results first (preferred)
      const { data: projectAnalysis } = await supabase
        .from('project_analysis')
        .select('analysis_metadata')
        .eq('project_id', projectId)
        .maybeSingle();

      let agentAnalysisResults = null;
      if (projectAnalysis?.analysis_metadata?.agent_analysis) {
        agentAnalysisResults = projectAnalysis.analysis_metadata.agent_analysis;
        logger.info('Using agent analysis results for blueprint generation', { projectId });
      }

      // Analyze the GitHub repository (fallback or supplement)
      const analysis = await GitHubService.analyzeRepository(
        projectId,
        userId
      );

      if (!analysis && !agentAnalysisResults) {
        logger.error('Failed to analyze GitHub repository and no agent analysis available');
        return null;
      }

      // Get project details
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('name, description, jira_project_key')
        .eq('id', projectId)
        .maybeSingle();

      if (projectError) {
        logger.error('Error fetching project for blueprint generation', {
          error: projectError,
          projectId,
        });
        throw new Error(`Failed to fetch project: ${projectError.message}`);
      }

      if (!project) {
        throw new Error('Project not found');
      }

      // Get Jira data if available
      let jiraData = null;
      if (project?.jira_project_key) {
        try {
          const jiraIssues = await JiraService.getProjectIssues(
            userId,
            project.jira_project_key
          );
          jiraData = {
            issues: jiraIssues.data,
            projectKey: project.jira_project_key,
          };
        } catch (error) {
          logger.warn('Failed to fetch Jira data:', error);
        }
      }

      // Build intelligent prompt with GitHub, agent analysis, and Jira context
      const prompt = this.buildIntelligentBlueprintPrompt(
        project?.name || 'Imported Project',
        project?.description || '',
        analysis,
        jiraData,
        agentAnalysisResults
      );

      const blueprintContent = await AnthropicService.generateCompletion({
        prompt,
        model: 'claude-3-5-sonnet-20241022',
        temperature: 0.7,
        max_tokens: 8000,
        userApiKeyId: undefined, // Will use platform key or user key from context
      });

      const parsedBlueprint = this.parseAIResponse(blueprintContent.content);

      // Store blueprint in project_blueprints table
      const { data: blueprint, error } = await supabase
        .from('project_blueprints')
        .insert({
          project_id: projectId,
          user_id: userId,
          blueprint_type: 'technical',
          content: parsedBlueprint,
          is_locked: false,
          version: 1,
        })
        .select()
        .single();

      if (error) {
        logger.error('Error creating blueprint', { error, projectId, userId });
        throw error;
      }

      if (!blueprint) {
        logger.error('Blueprint not created', { projectId, userId });
        throw new Error('Failed to create blueprint');
      }

      return blueprint as Blueprint;
    } catch (error) {
      logger.error('GitHub blueprint generation failed:', error);
      return null;
    }
  }

  static async generateFromRequirements(
    userId: string,
    projectId: string,
    requirements: Requirements,
    _provider?: string
  ): Promise<Blueprint> {
    return this.generateBlueprint(projectId, userId, requirements);
  }

  static async generateBlueprint(
    projectId: string,
    userId: string,
    requirements: Requirements
  ): Promise<ValidatedBlueprint> {
    try {
      logger.info('Starting blueprint generation', { projectId, userId });

      const sharedKnowledge = await this.fetchSharedKnowledge(projectId);

      const prompt = this.buildBlueprintPrompt(requirements, sharedKnowledge);

      logger.info('Calling AI for blueprint generation', { projectId });

      const blueprintContent = await AnthropicService.generateCompletion({
        prompt,
        model: 'claude-3-5-sonnet-20241022',
        temperature: 0.7,
        max_tokens: 4000,
        userApiKeyId: undefined,
      }).catch((aiError) => {
        logger.error('AI completion failed', { error: aiError, projectId });

        const errorMessage = aiError instanceof Error ? aiError.message : String(aiError);

        if (errorMessage.includes('API key') || errorMessage.includes('not configured')) {
          throw new Error(
            'AI service not configured. Please contact support to enable AI-powered blueprint generation.'
          );
        }

        if (errorMessage.includes('timeout')) {
          throw new Error(
            'Blueprint generation timed out. The requirements may be too complex. Please try breaking them into smaller features.'
          );
        }

        if (errorMessage.includes('rate limit')) {
          throw new Error(
            'AI service rate limit exceeded. Please wait a few minutes and try again.'
          );
        }

        throw new Error(
          `AI blueprint generation failed: ${errorMessage}`
        );
      });

      logger.info('Parsing AI response', { projectId });

      const parsedBlueprint = this.parseAIResponse(blueprintContent.content);

      // Validate against CycloneSpec
      const validation = await validateBlueprintContent(blueprintContent.content);
      
      if (!validation.valid) {
        // Log warnings but don't fail generation for minor issues
        logger.warn('Blueprint validation warnings', {
          projectId,
          userId,
          errors: validation.errors.length,
          missingSections: validation.missingSections,
          suggestions: validation.suggestions,
        });

        // Apply sanitization to fix minor issues
        const sanitizedContent = validateAndSanitize(blueprintContent.content);
        
        // Re-validate sanitized content
        const finalValidation = await validateBlueprintContent(sanitizedContent);
        
        if (!finalValidation.valid) {
          throw new Error(`Critical validation failures: ${finalValidation.errors.map(e => e.message).join('; ')}`);
        }

        // Use sanitized content
        blueprintContent.content = sanitizedContent;
      } else {
        logger.info('Blueprint validation passed', {
          projectId,
          userId,
          coverage: validation.coverage,
        });
      }

      // Store with validation metadata
      const blueprint = await this.storeBlueprint(projectId, userId, blueprintContent, {
        validation: validation
      });

      return {
        ...blueprint,
        validation,
      };
    } catch (error) {
      logger.error('Error generating blueprint with validation', {
        error,
        projectId,
        userId,
      });
      throw error;
    }
  }

  private static async fetchSharedKnowledge(
    projectId: string
  ): Promise<string> {
    const { data: knowledge } = await supabase
      .from('shared_knowledge')
      .select('title, content, knowledge_type')
      .eq('project_id', projectId)
      .order('relevance_score', { ascending: false })
      .limit(10);

    if (!knowledge || knowledge.length === 0) {
      return 'No existing project knowledge available.';
    }

    return knowledge
      .map(k => `### ${k.title} (${k.knowledge_type})\n${k.content}`)
      .join('\n\n');
  }

  private static buildBlueprintPrompt(
    requirements: Requirements,
    sharedKnowledge: string
  ): string {
    return `You are an expert software architect. Generate a comprehensive technical blueprint for the following project.

# Project Requirements

**Project Name:** ${requirements.projectName}

**Description:** ${requirements.description}

**Features:**
${requirements.features.map((f, i) => `${i + 1}. ${f}`).join('\n')}

${requirements.constraints && requirements.constraints.length > 0 ? `**Constraints:**\n${requirements.constraints.map((c, i) => `${i + 1}. ${c}`).join('\n')}` : ''}

${requirements.targetAudience ? `**Target Audience:** ${requirements.targetAudience}` : ''}

${
  requirements.techStack
    ? `**Preferred Tech Stack:**
- Frontend: ${requirements.techStack.frontend || 'Not specified'}
- Backend: ${requirements.techStack.backend || 'Not specified'}
- Database: ${requirements.techStack.database || 'Not specified'}
- Infrastructure: ${requirements.techStack.infrastructure || 'Not specified'}`
    : ''
}

# Existing Project Knowledge

${sharedKnowledge}

# Task

Generate a detailed technical blueprint in the following JSON structure:

\`\`\`json
{
  "architecture": {
    "layers": ["Array of architecture layers, e.g., Presentation, Business Logic, Data Access"],
    "patterns": ["Array of design patterns to use, e.g., Repository, Factory, Observer"],
    "components": ["Array of major system components"]
  },
  "techStack": {
    "frontend": ["Specific frontend technologies"],
    "backend": ["Specific backend technologies"],
    "database": ["Database choices"],
    "infrastructure": ["Deployment and infrastructure tools"]
  },
  "standards": {
    "coding": ["Coding standards and conventions"],
    "testing": ["Testing strategies and requirements"],
    "documentation": ["Documentation requirements"]
  },
  "phases": [
    {
      "name": "Phase name",
      "description": "Phase description",
      "deliverables": ["List of deliverables"],
      "estimatedDays": 10
    }
  ],
  "risks": ["Array of project risks"],
  "assumptions": ["Array of assumptions made"]
}
\`\`\`

Provide ONLY the JSON object, no additional explanation.`;
  }

  /**
   * Build intelligent prompt with GitHub, agent analysis, and Jira analysis
   */
  private static buildIntelligentBlueprintPrompt(
    projectName: string,
    description: string,
    githubAnalysis: any,
    jiraData: any,
    agentAnalysisResults?: any
  ): string {
    return `# Project Analysis Blueprint Generation

You are a senior technical architect analyzing an existing codebase.

## Project Information
- **Name:** ${projectName}
- **Description:** ${description || 'No description provided'}

## GitHub Repository Analysis

### Tech Stack Detected
${JSON.stringify(githubAnalysis.techStack, null, 2)}

### Architecture Insights
- **Project Structure:** ${githubAnalysis.architecture.projectStructure}
- **Design Patterns:** ${githubAnalysis.architecture.designPatterns.join(', ')}
- **Conventions:** ${githubAnalysis.architecture.conventions.join(', ')}
- **File Organization:** ${githubAnalysis.architecture.fileOrganization}

### Codebase Metrics
- **Total Files:** ${githubAnalysis.totalFiles}
- **Estimated Lines of Code:** ${githubAnalysis.totalLines}

### Dependencies
${Object.keys(githubAnalysis.dependencies).length} dependencies detected

### Coding Standards
${githubAnalysis?.standards?.join(', ') || 'No standards detected'}

${
  agentAnalysisResults
    ? `## Agent-Based SDLC Phase Analysis

**Overall Completion:** ${agentAnalysisResults.overallCompletion}%

**Phase-by-Phase Analysis:**
${agentAnalysisResults.phases?.map((p: any) => `
### ${p.phaseName.toUpperCase()} Phase
- **Completeness:** ${p.mergedAnalysis?.overallCompleteness || 0}%
- **Architect Findings:** ${p.architectAnalysis?.findings?.slice(0, 3).join(', ') || 'None'}
- **Full Stack Findings:** ${p.fullStackAnalysis?.findings?.slice(0, 3).join(', ') || 'None'}
- **Detected Components:** ${p.mergedAnalysis?.detectedComponents?.slice(0, 5).join(', ') || 'None'}
- **Missing Components:** ${p.mergedAnalysis?.missingComponents?.slice(0, 3).join(', ') || 'None'}
- **Recommendations:** ${p.mergedAnalysis?.recommendations?.slice(0, 2).join('; ') || 'None'}
`).join('')}

**Tech Stack Detected:**
- Frontend: ${agentAnalysisResults.techStack?.frontend?.join(', ') || 'Not detected'}
- Backend: ${agentAnalysisResults.techStack?.backend?.join(', ') || 'Not detected'}
- Database: ${agentAnalysisResults.techStack?.database?.join(', ') || 'Not detected'}
`
    : ''
}

${
  jiraData
    ? `## Jira Project Data
- **Project Key:** ${jiraData.projectKey}
- **Total Issues:** ${jiraData.issues?.length || 0}
`
    : ''
}

## Task

Based on this existing codebase analysis, generate a comprehensive technical blueprint that:

1. **Reverse-engineers the architecture** from the existing code structure
2. **Documents the current tech stack** and identifies any missing components
3. **Identifies coding standards** and conventions in use
4. **Maps out the SDLC phases** needed to enhance/maintain this project
5. **Highlights gaps and improvements** based on best practices
6. **Provides a roadmap** for future development

Return a JSON object with this structure:

\`\`\`json
{
  "architecture": {
    "layers": ["Detected architecture layers"],
    "patterns": ["Design patterns found in codebase"],
    "components": ["Major system components identified"],
    "gaps": ["Architecture gaps to address"]
  },
  "techStack": {
    "frontend": ["Frontend technologies in use"],
    "backend": ["Backend technologies in use"],
    "database": ["Database technologies"],
    "infrastructure": ["Infrastructure and tooling"],
    "missing": ["Recommended additions"]
  },
  "standards": {
    "coding": ["Detected coding standards"],
    "testing": ["Testing approach found"],
    "documentation": ["Documentation state"],
    "recommendations": ["Improvements needed"]
  },
  "phases": [
    {
      "name": "Phase name (e.g., Code Audit, Refactoring, Enhancement)",
      "description": "What needs to be done",
      "deliverables": ["Specific deliverables"],
      "estimatedDays": 10,
      "priority": "high|medium|low"
    }
  ],
  "currentState": {
    "strengths": ["What's working well"],
    "weaknesses": ["What needs improvement"],
    "technicalDebt": ["Technical debt identified"]
  },
  "recommendations": ["Strategic recommendations for improvement"],
  "risks": ["Project risks identified"],
  "assumptions": ["Assumptions made during analysis"]
}
\`\`\`

Provide ONLY the JSON object, no additional explanation.`;
  }

  private static parseAIResponse(response: string): {
    architecture: Blueprint['architecture'];
    techStack: Blueprint['techStack'];
    standards: Blueprint['standards'];
    phases: Blueprint['phases'];
    risks: string[];
    assumptions: string[];
  } {
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in AI response');
      }

      const parsed = JSON.parse(jsonMatch[0]);

      return {
        architecture: parsed.architecture || {
          layers: [],
          patterns: [],
          components: [],
        },
        techStack: parsed.techStack || {
          frontend: [],
          backend: [],
          database: [],
          infrastructure: [],
        },
        standards: parsed.standards || {
          coding: [],
          testing: [],
          documentation: [],
        },
        phases: parsed.phases || [],
        risks: parsed.risks || [],
        assumptions: parsed.assumptions || [],
      };
    } catch (error) {
      logger.error('Failed to parse AI response', { 
        error, 
        errorMessage: error instanceof Error ? error.message : String(error),
        responseLength: response?.length || 0,
        responsePreview: response?.substring(0, 200) || 'No content',
      });
      
      // Instead of silent fallback, throw an error with context
      // This makes the failure explicit and allows callers to handle it appropriately
      throw new Error(
        `Failed to parse AI-generated blueprint. ` +
        `The AI response could not be parsed as valid JSON. ` +
        `Error: ${error instanceof Error ? error.message : String(error)}. ` +
        `Please try regenerating the blueprint or adjust the requirements.`
      );
    }
  }

  static async freezeBlueprint(blueprintId: string): Promise<void> {
    // Validate blueprint completion before freezing
    const { data: blueprint, error: fetchError } = await supabase
      .from('project_blueprints')
      .select(
        'scope, technical_spec, architecture, wireframes, schema_design, user_stories, tech_stack, standards, security_requirements'
      )
      .eq('id', blueprintId)
      .maybeSingle();

    if (fetchError) {
      logger.error('Error fetching blueprint for freeze validation', {
        error: fetchError,
        blueprintId,
      });
      throw new Error(`Failed to fetch blueprint: ${fetchError.message}`);
    }

    if (!blueprint) {
      throw new Error('Blueprint not found');
    }

    // Calculate completion percentage based on sections with meaningful content
    const sections = [
      blueprint.scope,
      blueprint.technical_spec,
      blueprint.architecture,
      blueprint.wireframes,
      blueprint.schema_design,
      blueprint.user_stories,
      blueprint.tech_stack,
      blueprint.standards,
      blueprint.security_requirements,
    ];

    const completedSections = sections.filter(section => {
      if (!section || typeof section !== 'object') return false;
      // Check if section has meaningful content (not just empty object or placeholder)
      const keys = Object.keys(section);
      if (keys.length === 0) return false;
      // Check if it's not just a placeholder
      const values = Object.values(section);
      const hasRealContent = values.some(val => {
        if (typeof val === 'string' && val.trim().length > 0) return true;
        if (Array.isArray(val) && val.length > 0) return true;
        if (
          typeof val === 'object' &&
          val !== null &&
          Object.keys(val).length > 0
        )
          return true;
        return false;
      });
      return hasRealContent;
    }).length;

    const completionPercentage = (completedSections / sections.length) * 100;

    // Require at least 80% completion (same as UI check)
    if (completionPercentage < 80) {
      throw new Error(
        `Blueprint completion (${completionPercentage.toFixed(1)}%) is below required threshold (80%). ` +
          `Complete more sections before freezing. Missing: ${sections.length - completedSections} of ${sections.length} sections.`
      );
    }

    // Freeze the blueprint
    const { error } = await supabase
      .from('project_blueprints')
      .update({
        status: 'frozen',
        frozen_at: new Date().toISOString(),
      })
      .eq('id', blueprintId);

    if (error) throw error;
  }

  static async lockBlueprint(
    blueprintId: string,
    projectId: string,
    userId: string
  ): Promise<ValidatedBlueprint> {
    try {
      // Get current blueprint
      const { data: currentBlueprint } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('id', blueprintId)
        .eq('project_id', projectId)
        .eq('created_by', userId)
        .single();

      if (!currentBlueprint) {
        throw new Error('Blueprint not found');
      }

      // Final validation before locking
      const validation = await validateBlueprintContent(currentBlueprint.content);
      
      if (!validation.valid) {
        logger.warn('Locking blueprint with validation issues', {
          blueprintId,
          projectId,
          errors: validation.errors.length,
          missing: validation.missingSections,
        });

        // Don't block locking for warnings, but log
        if (validation.errors.some(e => e.severity === 'error')) {
          throw new Error(`Cannot lock blueprint - critical validation errors: ${validation.errors.map(e => e.message).join('; ')}`);
        }
      }

      // Update to locked state
      const { data: lockedBlueprint, error } = await supabase
        .from('project_blueprints')
        .update({
          is_locked: true,
          locked_at: new Date().toISOString(),
          version: (parseInt(currentBlueprint.version) + 1).toString(),
          metadata: {
            ...currentBlueprint.metadata,
            locked_with_validation: validation,
          },
        })
        .eq('id', blueprintId)
        .select()
        .single();

      if (error) throw error;

      logger.info('Blueprint locked successfully', {
        blueprintId,
        projectId,
        validationPassed: validation.valid,
      });

      return {
        ...lockedBlueprint,
        validation,
      };
    } catch (error) {
      logger.error('Error locking blueprint with validation', {
        error,
        blueprintId,
        projectId,
        userId,
      });
      throw error;
    }
  }

  static async markBlueprintAsSynced(
    blueprintId: string,
    jiraProjectKey: string
  ): Promise<void> {
    const { error } = await supabase
      .from('project_blueprints')
      .update({
        jira_synced: true,
        jira_project_key: jiraProjectKey,
      })
      .eq('id', blueprintId);

    if (error) throw error;
  }

  static async getBlueprint(blueprintId: string): Promise<Blueprint | null> {
    // First try project_blueprints table (project-specific blueprints)
    const { data: projectBlueprint, error: projectError } = await supabase
      .from('project_blueprints')
      .select('*')
      .eq('id', blueprintId)
      .maybeSingle();

    if (projectError) {
      logger.error('Error fetching project blueprint', {
        error: projectError,
        blueprintId,
      });
    }

    if (!projectError && projectBlueprint) {
      return {
        id: projectBlueprint.id,
        project_id: projectBlueprint.project_id,
        name: projectBlueprint.blueprint_type,
        content: projectBlueprint.content,
        status: projectBlueprint.is_locked ? 'locked' : 'draft',
        created_at: projectBlueprint.created_at,
        updated_at: projectBlueprint.updated_at,
      } as Blueprint;
    }

    // Fallback to blueprints table (template blueprints)
    // Note: blueprints table has 'name' not 'title', and 'template_data' not 'content'
    const { data, error } = await supabase
      .from('blueprints')
      .select(
        'id, name, description, template_data, category, tags, is_active, is_public, user_id, created_at, updated_at'
      )
      .eq('id', blueprintId)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching template blueprint', { error, blueprintId });
      return null;
    }

    if (!data) {
      logger.warn('Blueprint not found in either table', { blueprintId });
      return null;
    }

    // Map blueprints table schema to Blueprint interface
    return {
      id: data.id,
      project_id: '', // Template blueprints don't have project_id
      title: data.name || 'Untitled Blueprint',
      description: data.description || '',
      content: data.template_data || {},
      status: 'draft',
      created_at: data.created_at,
      updated_at: data.updated_at,
    } as Blueprint;
  }

  /**
   * Generate manifest from blueprint
   */
  static async generateManifest(
    blueprintId: string,
    projectId: string
  ): Promise<{ success: boolean; manifestId?: string; error?: string }> {
    try {
      const blueprint = await this.getBlueprint(blueprintId);
      if (!blueprint) {
        return { success: false, error: 'Blueprint not found' };
      }

      // Generate manifest based on blueprint
      const manifest = {
        blueprint_id: blueprintId,
        project_id: projectId,
        content: {
          architecture: blueprint.content?.architecture || {},
          techStack: blueprint.content?.techStack || {},
          implementation: {
            phases: blueprint.content?.phases || [],
            tasks: [],
            dependencies: [],
          },
        },
        status: 'pending',
        created_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('project_manifests')
        .insert(manifest)
        .select()
        .single();

      if (error) {
        logger.error('Error creating manifest', {
          error,
          projectId,
          blueprintId,
        });
        return { success: false, error: error.message };
      }

      if (!data) {
        logger.error('Manifest not created', { projectId, blueprintId });
        return { success: false, error: 'Failed to create manifest' };
      }

      return { success: true, manifestId: data.id };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Generate Jira plan from blueprint
   */
  static async generateJiraPlan(
    blueprintId: string,
    projectId: string,
    userId?: string,
    jiraProjectKey?: string
  ): Promise<{ success: boolean; error?: string; epics?: any[] }> {
    try {
      const blueprint = await this.getBlueprint(blueprintId);
      if (!blueprint) {
        return { success: false, error: 'Blueprint not found' };
      }

      // Get userId if not provided
      let finalUserId = userId;
      if (!finalUserId) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          finalUserId = user?.id;
        } catch (error) {
          logger.warn('Could not get userId for Jira plan generation', { error });
        }
      }

      if (!finalUserId) {
        return { success: false, error: 'User ID is required for Jira plan generation' };
      }

      // Import and use JiraPlanGeneratorService
      const { JiraPlanGeneratorService } = await import('../jiraPlanGeneratorService');
      
      // Generate Jira plan using the actual service
      const epics = await JiraPlanGeneratorService.generateFromBlueprint(
        finalUserId,
        projectId,
        blueprintId,
        jiraProjectKey || 'PROJ' // Default project key if not provided
      );

      logger.info('Jira plan generated successfully', { blueprintId, projectId, epicsCount: epics.length });
      
      return { success: true, epics };
    } catch (error: any) {
      logger.error('Error generating Jira plan', { error, blueprintId, projectId });
      return { success: false, error: error.message || 'Failed to generate Jira plan' };
    }
  }

  /**
   * Start build mode with blueprint
   */
  static async startBuildMode(
    blueprintId: string,
    projectId: string,
    userId: string
  ): Promise<{ success: boolean; sessionId?: string; error?: string }> {
    try {
      const blueprint = await this.getBlueprint(blueprintId);
      if (!blueprint) {
        return { success: false, error: 'Blueprint not found' };
      }

      // Lock blueprint before starting build mode
      const lockStatus = await this.checkLockStatus(blueprintId);
      if (!lockStatus.isLocked) {
        const lockResult = await this.lockBlueprint(blueprintId, projectId, userId, 'Locked for build mode');
        if (!lockResult.success) {
          return { success: false, error: lockResult.error || 'Failed to lock blueprint' };
        }
      }

      // Create build session
      const { data: session, error } = await supabase
        .from('build_mode_sessions')
        .insert({
          project_id: projectId,
          blueprint_id: blueprintId,
          status: 'pending',
          metadata: {
            started_by: userId,
            started_at: new Date().toISOString(),
          },
        })
        .select()
        .single();

      if (error) {
        logger.error('Error creating build session', {
          error,
          projectId,
          blueprintId,
          userId,
        });
        return { success: false, error: error.message };
      }

      if (!session) {
        logger.error('Build session not created', {
          projectId,
          blueprintId,
          userId,
        });
        return { success: false, error: 'Failed to create build session' };
      }

      return { success: true, sessionId: session.id };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  // ==========================================================================
  // Validation (from blueprintValidationService.ts)
  // ==========================================================================

  static async validateBlueprint(blueprint: Blueprint): Promise<{
    valid: boolean;
    results: any[];
  }> {
    // Existing validation logic
    const existingResults = await this.existingValidationLogic(blueprint);

    // Add schema validation
    const schemaValidation = await validateBlueprintContent(blueprint.content);
    
    return {
      valid: existingResults.every(r => r.passed) && schemaValidation.valid,
      results: [
        ...existingResults,
        {
          check: 'schema_validation',
          status: schemaValidation.valid ? 'pass' : 'fail',
          details: schemaValidation,
          message: schemaValidation.valid ? 'Schema validation passed' : 'Schema validation failed',
        },
      ],
    };
  }

  static async validateWithAI(
    blueprintId: string,
    userId: string,
    userApiKeyId?: string
  ): Promise<ValidationResult> {
    const basicValidation = await this.validateBlueprint(blueprintId, userId);

    const { data: blueprint } = await supabase
      .from('project_blueprints')
      .select('*')
      .eq('id', blueprintId)
      .maybeSingle();

    if (!blueprint) {
      return basicValidation;
    }

    const prompt = this.buildAIValidationPrompt(blueprint);

    try {
      const aiResponse = await AnthropicService.generateCompletion({
        prompt,
        model: 'claude-3-5-sonnet-20241022',
        temperature: 0.3,
        max_tokens: 4096,
        userApiKeyId,
      });

      const aiReview = this.parseAIValidationResponse(aiResponse.content);
      basicValidation.aiReview = aiReview;

      aiReview.weaknesses.forEach((weakness) => {
        basicValidation.warnings.push({
          field: 'ai_review',
          message: weakness,
          severity: 'warning',
        });
      });

      aiReview.improvements.forEach((improvement) => {
        basicValidation.suggestions.push(improvement);
      });

      await this.saveValidationResult(blueprintId, basicValidation);
    } catch (error) {
      logger.error('AI validation failed:', error);
    }

    return basicValidation;
  }

  static async getValidationHistory(blueprintId: string): Promise<ValidationResult[]> {
    const { data, error } = await supabase
      .from('blueprint_validations')
      .select('*')
      .eq('blueprint_id', blueprintId)
      .order('validated_at', { ascending: false })
      .limit(10);

    if (error) {
      logger.error('Failed to fetch validation history:', error);
      return [];
    }

    return (data || []).map((record) => ({
      isValid: record.is_valid,
      score: record.score,
      errors: record.errors || [],
      warnings: record.warnings || [],
      suggestions: record.suggestions || [],
      aiReview: record.ai_review,
    }));
  }

  // ==========================================================================
  // Locking (from blueprintLockService.ts)
  // ==========================================================================

  static async unlockBlueprint(
    blueprintId: string,
    userId: string,
    reason: string = 'Manual unlock'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      logger.info('Unlocking blueprint', { blueprintId, userId, reason });

      // Check if user has admin permissions
      const isAdmin = await this.checkAdminPermissions(userId);
      if (!isAdmin) {
        logger.warn('Unauthorized unlock attempt', { blueprintId, userId });
        await this.logModificationAttempt(blueprintId, userId, 'unlock', true, 'Insufficient permissions');
        return { success: false, error: 'Insufficient permissions to unlock blueprint' };
      }

      // Get blueprint
      const { data: blueprint, error: fetchError } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('id', blueprintId)
        .single();

      if (fetchError || !blueprint) {
        return { success: false, error: 'Blueprint not found' };
      }

      // Check if locked
      if (!blueprint.is_locked) {
        return { success: false, error: 'Blueprint is not locked' };
      }

      // Unlock blueprint
      const { error: updateError } = await supabase
        .from('project_blueprints')
        .update({
          is_locked: false,
          locked_at: null,
          locked_by: null,
          lock_reason: null,
        })
        .eq('id', blueprintId);

      if (updateError) {
        logger.error('Error unlocking blueprint', { error: updateError, blueprintId });
        return { success: false, error: 'Failed to unlock blueprint' };
      }

      // Log unlock event
      await this.logLockEvent(blueprintId, userId, 'unlock', reason);

      logger.info('Blueprint unlocked successfully', { blueprintId, userId });

      return { success: true };
    } catch (error) {
      logger.error('Error in unlockBlueprint', { error, blueprintId });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  static async checkLockStatus(blueprintId: string): Promise<BlueprintLockStatus> {
    try {
      const { data: blueprint, error } = await supabase
        .from('project_blueprints')
        .select('is_locked, locked_at, locked_by, lock_reason')
        .eq('id', blueprintId)
        .single();

      if (error || !blueprint) {
        return {
          isLocked: false,
          canModify: true,
        };
      }

      return {
        isLocked: blueprint.is_locked || false,
        lockedAt: blueprint.locked_at ? new Date(blueprint.locked_at) : undefined,
        lockedBy: blueprint.locked_by || undefined,
        reason: blueprint.lock_reason || undefined,
        canModify: !blueprint.is_locked,
      };
    } catch (error) {
      logger.error('Error checking lock status', { error, blueprintId });
      return {
        isLocked: false,
        canModify: true,
      };
    }
  }

  static async attemptModification(
    blueprintId: string,
    userId: string,
    modificationType: 'edit' | 'delete'
  ): Promise<{ allowed: boolean; reason?: string }> {
    try {
      // Check lock status
      const lockStatus = await this.checkLockStatus(blueprintId);

      if (lockStatus.isLocked) {
        logger.warn('Modification attempt on locked blueprint', {
          blueprintId,
          userId,
          modificationType,
        });

        await this.logModificationAttempt(
          blueprintId,
          userId,
          modificationType,
          true,
          `Blueprint is locked: ${lockStatus.reason || 'In execution'}`
        );

        return {
          allowed: false,
          reason: `Blueprint is locked and cannot be modified. ${lockStatus.reason || 'Blueprint is currently in execution.'}`,
        };
      }

      return { allowed: true };
    } catch (error) {
      logger.error('Error in attemptModification', { error, blueprintId });
      return {
        allowed: false,
        reason: 'Error checking modification permissions',
      };
    }
  }

  static async autoLockOnBuildStart(
    projectId: string,
    userId: string,
    buildSessionId: string
  ): Promise<{ success: boolean; blueprintId?: string; error?: string }> {
    try {
      logger.info('Auto-locking blueprint for build session', {
        projectId,
        buildSessionId,
      });

      // Get latest blueprint for project
      const { data: blueprint, error: fetchError } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (fetchError || !blueprint) {
        return { success: false, error: 'No blueprint found for project' };
      }

      // Lock the blueprint
      const lockResult = await this.lockBlueprint(
        blueprint.id,
        projectId,
        userId,
        `Locked automatically for build session: ${buildSessionId}`
      );

      if (!lockResult.success) {
        return { success: false, error: lockResult.error };
      }

      // Update build session with blueprint ID
      await supabase
        .from('build_mode_sessions')
        .update({ blueprint_id: blueprint.id })
        .eq('id', buildSessionId);

      logger.info('Blueprint auto-locked for build session', {
        blueprintId: blueprint.id,
        buildSessionId,
      });

      return { success: true, blueprintId: blueprint.id };
    } catch (error) {
      logger.error('Error in autoLockOnBuildStart', { error, projectId });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  static async autoUnlockOnBuildComplete(
    buildSessionId: string,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      logger.info('Auto-unlocking blueprint for build session', { buildSessionId });

      // Get build session
      const { data: session, error: sessionError } = await supabase
        .from('build_mode_sessions')
        .select('blueprint_id, status')
        .eq('id', buildSessionId)
        .single();

      if (sessionError || !session) {
        return { success: false, error: 'Build session not found' };
      }

      if (!session.blueprint_id) {
        return { success: true }; // No blueprint to unlock
      }

      // Only unlock if build is completed or failed
      if (session.status === 'completed' || session.status === 'failed') {
        const unlockResult = await this.unlockBlueprint(
          session.blueprint_id,
          userId,
          `Unlocked automatically after build session ${session.status}: ${buildSessionId}`
        );

        if (!unlockResult.success) {
          return { success: false, error: unlockResult.error };
        }

        logger.info('Blueprint auto-unlocked after build session', {
          blueprintId: session.blueprint_id,
          buildSessionId,
        });

        return { success: true };
      }

      return { success: true }; // Build not complete, don't unlock
    } catch (error) {
      logger.error('Error in autoUnlockOnBuildComplete', { error, buildSessionId });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  static async getLockHistory(blueprintId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('blueprint_lock_events')
        .select('*, users(email)')
        .eq('blueprint_id', blueprintId)
        .order('timestamp', { ascending: false })
        .limit(50);

      if (error) {
        logger.error('Error fetching lock history', { error, blueprintId });
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error('Error in getLockHistory', { error, blueprintId });
      return [];
    }
  }

  static async getModificationAttempts(blueprintId: string): Promise<BlueprintModificationAttempt[]> {
    try {
      const { data, error } = await supabase
        .from('blueprint_modification_attempts')
        .select('*')
        .eq('blueprint_id', blueprintId)
        .eq('denied', true)
        .order('attempted_at', { ascending: false })
        .limit(50);

      if (error) {
        logger.error('Error fetching modification attempts', { error, blueprintId });
        return [];
      }

      return (data || []).map((attempt) => ({
        blueprintId: attempt.blueprint_id,
        userId: attempt.user_id,
        attemptedAt: new Date(attempt.attempted_at),
        modificationType: attempt.modification_type,
        denied: attempt.denied,
        reason: attempt.reason,
      }));
    } catch (error) {
      logger.error('Error in getModificationAttempts', { error, blueprintId });
      return [];
    }
  }

  // ==========================================================================
  // Sync (from blueprintPlanSyncService.ts)
  // ==========================================================================

  static async syncBlueprintToPlanTree(
    projectId: string,
    sectionId: string,
    sectionData: any
  ): Promise<void> {
    try {
      logger.info('Syncing blueprint section to plan tree', { projectId, sectionId });

      // Map blueprint sections to plan tree nodes
      const sectionToNodeMap: Record<string, string> = {
        'overview': 'requirements',
        'scope': 'requirements',
        'requirements': 'requirements',
        'architecture': 'architecture',
        'techstack': 'tech-stack',
        'wireframes': 'wireframes',
        'schema': 'schema',
        'stories': 'user-stories',
        'standards': 'coding-standards',
        'theme': 'theme',
        'timeline': 'phases',
        'gaps': 'validation',
      };

      const nodeId = sectionToNodeMap[sectionId];
      if (!nodeId) {
        logger.warn('No plan tree node mapping for blueprint section', { sectionId });
        return;
      }

      // Update plan tree node data via PlanTreeService
      await PlanTreeService.updatePlanSection(projectId, nodeId, sectionData);

      // Also update the blueprint content directly
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('content')
        .eq('project_id', projectId)
        .maybeSingle();

      if (blueprint) {
        const updatedContent = {
          ...blueprint.content,
          [sectionId]: sectionData,
        };

        await supabase
          .from('project_blueprints')
          .update({
            content: updatedContent,
            updated_at: new Date().toISOString(),
          })
          .eq('project_id', projectId);
      }

      logger.info('Blueprint section synced to plan tree', { projectId, sectionId, nodeId });
    } catch (error) {
      logger.error('Error syncing blueprint to plan tree', { error, projectId, sectionId });
      throw error;
    }
  }

  static async syncPlanTreeToBlueprint(
    projectId: string,
    nodeId: string,
    nodeData: any
  ): Promise<void> {
    try {
      logger.info('Syncing plan tree node to blueprint', { projectId, nodeId });

      // Map plan tree nodes to blueprint sections
      const nodeToSectionMap: Record<string, string> = {
        'requirements': 'requirements',
        'scope': 'scope',
        'architecture': 'architecture',
        'tech-stack': 'techstack',
        'schema': 'schema',
        'api-design': 'api_design',
        'wireframes': 'wireframes',
        'theme': 'theme',
        'coding-standards': 'standards',
        'feature-dev': 'development_plan',
        'unit-testing': 'unit_testing',
        'integration-testing': 'integration_testing',
        'security-testing': 'security_testing',
        'performance-testing': 'performance_testing',
        'user-acceptance-testing': 'user_acceptance_testing',
        'ci-cd': 'cicd_pipeline',
        'monitoring': 'monitoring',
        'release-management': 'release_management',
        'bug-fixing': 'bug_fixing',
        'enhancements': 'enhancements',
        'documentation-updates': 'documentation_updates',
      };

      const sectionId = nodeToSectionMap[nodeId];
      if (!sectionId) {
        logger.warn('No blueprint section mapping for plan tree node', { nodeId });
        return;
      }

      // Update blueprint content
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('content')
        .eq('project_id', projectId)
        .maybeSingle();

      if (blueprint) {
        const updatedContent = {
          ...blueprint.content,
          [sectionId]: nodeData,
        };

        await supabase
          .from('project_blueprints')
          .update({
            content: updatedContent,
            updated_at: new Date().toISOString(),
          })
          .eq('project_id', projectId);
      } else {
        // Create blueprint if it doesn't exist
        await supabase
          .from('project_blueprints')
          .insert({
            project_id: projectId,
            content: {
              [sectionId]: nodeData,
            },
            blueprint_type: 'technical',
            version: 1,
          });
      }

      logger.info('Plan tree node synced to blueprint', { projectId, nodeId, sectionId });
    } catch (error) {
      logger.error('Error syncing plan tree to blueprint', { error, projectId, nodeId });
      throw error;
    }
  }

  static async refreshBoth(
    projectId: string,
    userId: string
  ): Promise<{
    blueprint: any;
    planTree: any;
  }> {
    try {
      logger.info('Refreshing blueprint and plan tree', { projectId });

      // Get latest blueprint
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Regenerate plan tree (which reads from blueprint)
      const planTree = await PlanTreeService.generatePlanTree(projectId, userId);

      return {
        blueprint,
        planTree,
      };
    } catch (error) {
      logger.error('Error refreshing blueprint and plan tree', { error, projectId });
      throw error;
    }
  }

  static async initializeFromGitHubAnalysis(
    projectId: string,
    userId: string
  ): Promise<void> {
    try {
      logger.info('Initializing blueprint from GitHub analysis', { projectId });

      // Check if blueprint already exists
      const { data: existingBlueprint } = await supabase
        .from('project_blueprints')
        .select('id')
        .eq('project_id', projectId)
        .maybeSingle();

      if (existingBlueprint) {
        logger.info('Blueprint already exists, skipping initialization', { projectId });
        return;
      }

      // Generate blueprint from GitHub analysis
      const blueprint = await this.generateFromGitHub(projectId, userId);

      if (blueprint) {
        logger.info('Blueprint initialized from GitHub analysis', {
          projectId,
          blueprintId: blueprint.id,
        });
      } else {
        logger.warn('Failed to generate blueprint from GitHub analysis', { projectId });
      }
    } catch (error) {
      logger.error('Error initializing blueprint from GitHub analysis', {
        error,
        projectId,
      });
      throw error;
    }
  }

  static async getCompletionStatus(
    projectId: string,
    userId: string
  ): Promise<{
    blueprintCompletion: number;
    planTreeCompletion: number;
    synced: boolean;
  }> {
    try {
      // Get blueprint completion
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('content')
        .eq('project_id', projectId)
        .maybeSingle();

      const blueprintCompletion = this.calculateBlueprintCompletion(blueprint?.content);

      // Get plan tree completion
      const planTree = await PlanTreeService.generatePlanTree(projectId, userId);
      const planTreeCompletion = planTree.overallCompletion;

      // Check if they're synced (within 5% difference)
      const synced = Math.abs(blueprintCompletion - planTreeCompletion) <= 5;

      return {
        blueprintCompletion,
        planTreeCompletion,
        synced,
      };
    } catch (error) {
      logger.error('Error getting completion status', { error, projectId });
      return {
        blueprintCompletion: 0,
        planTreeCompletion: 0,
        synced: false,
      };
    }
  }

  // ==========================================================================
  // Tree (from blueprintTreeService.ts)
  // ==========================================================================

  static async buildBlueprintTree(projectId: string): Promise<BlueprintTreeData> {
    try {
      logger.info('Building blueprint tree', { projectId });

      // Load all project data in parallel
      const [
        atomicRequirements,
        userStories,
        stakeholders,
        wireframes,
        project
      ] = await Promise.all([
        RequirementsService.getAtomicRequirements(projectId),
        RequirementsService.getUserStories(projectId),
        RequirementsService.getStakeholders(projectId),
        RequirementsService.getWireframes(projectId),
        this.getProjectDetails(projectId)
      ]);

      // Build tree structure
      const root: BlueprintTreeNode = {
        id: 'root',
        type: 'root',
        label: 'Project Blueprint',
        icon: '📋',
        children: []
      };

      const sections = {
        requirements: this.buildRequirementsSection(atomicRequirements, userStories, stakeholders),
        architecture: this.buildArchitectureSection(project),
        conventions: this.buildConventionsSection(project),
        security: this.buildSecuritySection(project),
        design: this.buildDesignSection(wireframes, project),
        phases: this.buildPhasesSection(project)
      };

      root.children = [
        sections.requirements,
        sections.architecture,
        sections.conventions,
        sections.security,
        sections.design,
        sections.phases
      ];

      return { root, sections };
    } catch (error) {
      logger.error('Error building blueprint tree', { error, projectId });
      throw error;
    }
  }

  static async getNodeData(nodeId: string, projectId: string): Promise<{
    node: BlueprintTreeNode;
    data: any;
    promptContext: string;
    suggestions: string[];
  }> {
    try {
      const [node, data] = await this.loadNodeData(nodeId, projectId);
      const promptContext = this.generatePromptContext(node, data);
      const suggestions = this.generatePromptSuggestions(node, data);

      return {
        node,
        data,
        promptContext,
        suggestions
      };
    } catch (error) {
      logger.error('Error getting node data', { error, nodeId, projectId });
      throw error;
    }
  }

  static async updateNodeFromPrompt(
    nodeId: string,
    projectId: string,
    userId: string,
    prompt: string,
    response: string
  ): Promise<ValidatedBlueprint> {
    try {
      // Get existing blueprint
      const { data: existingBlueprint } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('id', nodeId)
        .eq('project_id', projectId)
        .eq('created_by', userId)
        .single();

      if (!existingBlueprint) {
        throw new Error('Blueprint not found');
      }

      // Update content
      const updatedContent = {
        ...existingBlueprint.content,
        // Apply update logic here
        [nodeId]: {
          ...existingBlueprint.content[nodeId],
          content: response,
          updated_at: new Date().toISOString(),
        },
      };

      // Validate updated content
      const validation = await validateBlueprintContent(updatedContent);
      
      if (!validation.valid) {
        logger.warn('Blueprint node update validation warnings', {
          nodeId,
          projectId,
          errors: validation.errors.length,
        });

        // Sanitize
        const sanitizedContent = validateAndSanitize(updatedContent);
        
        const finalValidation = await validateBlueprintContent(sanitizedContent);
        if (!finalValidation.valid) {
          throw new Error(`Cannot update blueprint node - validation failed: ${finalValidation.errors.map(e => e.message).join('; ')}`);
        }

        // Use sanitized
        updatedContent.content = sanitizedContent;
      }

      // Update in database
      const { data: updatedBlueprint, error } = await supabase
        .from('project_blueprints')
        .update({
          content: updatedContent.content,
          updated_at: new Date().toISOString(),
        })
        .eq('id', nodeId)
        .select()
        .single();

      if (error) throw error;

      return {
        ...updatedBlueprint,
        validation,
      };
    } catch (error) {
      logger.error('Error updating blueprint node with validation', {
        error,
        nodeId,
        projectId,
        userId,
      });
      throw error;
    }
  }

  static async searchBlueprintTree(
    projectId: string,
    query: string
  ): Promise<BlueprintTreeNode[]> {
    try {
      const tree = await this.buildBlueprintTree(projectId);
      const results: BlueprintTreeNode[] = [];

      this.searchTreeRecursive(tree.root, query.toLowerCase(), results);
      
      return results;
    } catch (error) {
      logger.error('Error searching blueprint tree', { error, projectId, query });
      return [];
    }
  }

  // ==========================================================================
  // Gap Analysis (from gapAnalysisService.ts)
  // ==========================================================================

  static async analyzeProject(projectId: string): Promise<GapAnalysisReport> {
    try {
      logger.info('Starting gap analysis', { projectId });

      // Build architecture tree
      const tree = await FullStackArchitectureService.buildArchitectureTree(projectId);

      // Run all gap detection analyses
      const gaps: GapAnalysisResult[] = [];

      gaps.push(...await this.detectSchemaBackendGaps(tree));
      gaps.push(...await this.detectBackendFrontendGaps(tree));
      gaps.push(...await this.detectUnusedResources(tree));
      gaps.push(...await this.detectMissingImplementations(tree));
      gaps.push(...await this.detectOrphanedNodes(tree));

      // Calculate summary statistics
      const summary = this.calculateGapSummary(gaps);
      
      // Calculate integration score
      const integrationScore = this.calculateIntegrationScore(tree, gaps);

      // Generate recommendations
      const recommendations = this.generateGapRecommendations(gaps, tree);

      const report: GapAnalysisReport = {
        projectId,
        analyzedAt: new Date().toISOString(),
        gaps,
        summary,
        integrationScore,
        recommendations,
      };

      // Save report to database
      await this.saveGapAnalysisReport(projectId, report);

      logger.info('Gap analysis completed', {
        projectId,
        totalGaps: gaps.length,
        integrationScore,
      });

      return report;
    } catch (error) {
      logger.error('Error during gap analysis', { error, projectId });
      throw error;
    }
  }

  static async analyzeBlueprint(
    projectId: string,
    blueprintId: string
  ): Promise<{ 
    isComplete: boolean; 
    criticalGaps?: GapAnalysisResult[]; 
    warnings?: GapAnalysisResult[]; 
    analyzedAt?: string;
    architectureAnalysis?: {
      layerConnectivity: boolean;
      brokenRelationships: string[];
      missingLayers: string[];
      dataFlowIssues: string[];
    };
    featureCompleteness?: {
      implemented: number;
      total: number;
      missingFeatures: string[];
      incompleteRequirements: string[];
    };
  }> {
    try {
      // Run the full project analysis
      const report = await this.analyzeProject(projectId);
      
      // Get blueprint for deep analysis
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('id', blueprintId)
        .single();

      if (!blueprint) {
        throw new Error('Blueprint not found');
      }

      // Deep architecture analysis
      const architectureAnalysis = await this.analyzeArchitectureRelationships(blueprint.content);
      
      // Feature completeness analysis
      const featureCompleteness = await this.analyzeFeatureCompleteness(blueprint.content, projectId);
      
      // Filter gaps by severity for blueprint validation
      const criticalGaps = report.gaps.filter(g => g.severity === 'critical');
      const warnings = report.gaps.filter(g => g.severity === 'high' || g.severity === 'medium');
      
      // Add architecture and feature gaps to critical gaps
      if (!architectureAnalysis.layerConnectivity) {
        criticalGaps.push({
          id: 'arch-connectivity',
          type: 'architecture',
          severity: 'critical',
          layer: 'all',
          message: 'Architecture layers are not properly connected',
          suggestedFix: 'Define proper interfaces between layers',
          impact: 'System will not function without layer connectivity',
        });
      }
      
      if (architectureAnalysis.brokenRelationships.length > 0) {
        criticalGaps.push({
          id: 'arch-relationships',
          type: 'architecture',
          severity: 'critical',
          layer: 'relationships',
          message: `Found ${architectureAnalysis.brokenRelationships.length} broken relationships`,
          suggestedFix: architectureAnalysis.brokenRelationships.join(', '),
          impact: 'Components cannot communicate properly',
        });
      }
      
      if (featureCompleteness.missingFeatures.length > 0) {
        warnings.push({
          id: 'feature-gaps',
          type: 'feature',
          severity: 'high',
          layer: 'features',
          message: `${featureCompleteness.missingFeatures.length} features not fully implemented`,
          suggestedFix: `Complete: ${featureCompleteness.missingFeatures.join(', ')}`,
          impact: 'Application may not meet all requirements',
        });
      }
      
      // Blueprint is considered complete if there are no critical gaps and good coverage
      const isComplete = criticalGaps.length === 0 && 
                        report.integrationScore >= 70 &&
                        architectureAnalysis.layerConnectivity &&
                        (featureCompleteness.implemented / featureCompleteness.total) >= 0.8;
      
      return {
        isComplete,
        criticalGaps,
        warnings,
        analyzedAt: report.analyzedAt,
        architectureAnalysis,
        featureCompleteness,
      };
    } catch (error) {
      logger.error('Error analyzing blueprint', { error, projectId, blueprintId });
      // Return incomplete status on error
      return {
        isComplete: false,
        criticalGaps: [],
        warnings: [],
      };
    }
  }

  /**
   * Analyze architecture layer relationships and connectivity
   */
  private static async analyzeArchitectureRelationships(blueprintContent: any): Promise<{
    layerConnectivity: boolean;
    brokenRelationships: string[];
    missingLayers: string[];
    dataFlowIssues: string[];
  }> {
    const requiredLayers = ['presentation', 'business', 'data', 'infrastructure'];
    const architecture = blueprintContent.architecture || {};
    const layers = architecture.layers || {};
    
    // Check for missing layers
    const missingLayers = requiredLayers.filter(layer => !layers[layer]);
    
    // Analyze relationships between layers
    const brokenRelationships: string[] = [];
    const dataFlowIssues: string[] = [];
    
    // Check presentation -> business layer connection
    if (layers.presentation && layers.business) {
      const hasApiConnection = layers.presentation.apis?.some((api: any) => 
        layers.business.services?.some((service: any) => service.exposes?.includes(api.endpoint))
      );
      if (!hasApiConnection) {
        brokenRelationships.push('Presentation layer not connected to business services');
      }
    }
    
    // Check business -> data layer connection
    if (layers.business && layers.data) {
      const hasDataConnection = layers.business.services?.some((service: any) => 
        service.dependencies?.some((dep: any) => layers.data.repositories?.includes(dep))
      );
      if (!hasDataConnection) {
        brokenRelationships.push('Business layer not connected to data repositories');
      }
    }
    
    // Check data flow completeness
    const apis = blueprintContent.apis || [];
    for (const api of apis) {
      const hasCompleteFlow = this.validateDataFlow(api, layers);
      if (!hasCompleteFlow) {
        dataFlowIssues.push(`API ${api.path} has incomplete data flow`);
      }
    }
    
    const layerConnectivity = missingLayers.length === 0 && brokenRelationships.length === 0;
    
    return {
      layerConnectivity,
      brokenRelationships,
      missingLayers,
      dataFlowIssues,
    };
  }

  /**
   * Validate data flow for an API through all layers
   */
  private static validateDataFlow(api: any, layers: any): boolean {
    // Check if API flows through all layers properly
    const hasController = layers.presentation?.controllers?.some((c: any) => c.handles === api.path);
    const hasService = layers.business?.services?.some((s: any) => s.implements?.includes(api.path));
    const hasDataAccess = layers.data?.repositories?.some((r: any) => r.usedBy?.includes(api.path));
    
    return hasController && hasService && hasDataAccess;
  }

  /**
   * Analyze feature completeness
   */
  private static async analyzeFeatureCompleteness(blueprintContent: any, projectId: string): Promise<{
    implemented: number;
    total: number;
    missingFeatures: string[];
    incompleteRequirements: string[];
  }> {
    const features = blueprintContent.features || [];
    const requirements = blueprintContent.requirements || [];
    
    // Get implemented features from codebase
    const { data: implementations } = await supabase
      .from('feature_implementations')
      .select('feature_id, status')
      .eq('project_id', projectId);
    
    const implementedFeatures = implementations?.filter(i => i.status === 'complete').map(i => i.feature_id) || [];
    const missingFeatures = features
      .filter((f: any) => !implementedFeatures.includes(f.id))
      .map((f: any) => f.name || f.title);
    
    // Check requirements coverage
    const incompleteRequirements: string[] = [];
    for (const req of requirements) {
      const hasImplementation = await this.checkRequirementImplementation(req, blueprintContent, projectId);
      if (!hasImplementation) {
        incompleteRequirements.push(req.title || req.description);
      }
    }
    
    return {
      implemented: implementedFeatures.length,
      total: features.length,
      missingFeatures,
      incompleteRequirements,
    };
  }

  /**
   * Check if a requirement has been implemented
   */
  private static async checkRequirementImplementation(requirement: any, blueprintContent: any, projectId: string): Promise<boolean> {
    // Check if requirement has associated tests
    const tests = blueprintContent.tests || [];
    const hasTests = tests.some((t: any) => t.requirements?.includes(requirement.id));
    
    // Check if requirement has code implementation
    const { data: codeFiles } = await supabase
      .from('file_registry')
      .select('file_path')
      .eq('project_id', projectId)
      .ilike('tags', `%${requirement.id}%`);
    
    return hasTests && (codeFiles?.length || 0) > 0;
  }

  static async getLatestGapAnalysisReport(projectId: string): Promise<GapAnalysisReport | null> {
    try {
      const { data, error } = await supabase
        .from('project_analysis')
        .select('analysis_metadata, analyzed_at')
        .eq('project_id', projectId)
        .order('analyzed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      // Extract gap analysis from analysis_metadata
      if (data?.analysis_metadata?.gap_analysis) {
        return data.analysis_metadata.gap_analysis as GapAnalysisReport;
      }

      return null;
    } catch (error) {
      logger.error('Error getting latest gap analysis report', { error, projectId });
      return null;
    }
  }

  // ==========================================================================
  // SDLC Population (from sdlcPopulationService.ts)
  // ==========================================================================

  static async populatePlanTreeFromAnalysis(
    projectId: string,
    analysisResults: AgentAnalysisResult
  ): Promise<void> {
    try {
      logger.info('Populating SDLC tree from agent analysis', { projectId });

      // Get or create blueprint
      let { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('project_id', projectId)
        .maybeSingle();

      if (!blueprint) {
        // Create initial blueprint
        const { data: newBlueprint, error: createError } = await supabase
          .from('project_blueprints')
          .insert({
            project_id: projectId,
            blueprint_type: 'technical',
            content: {},
            version: 1,
            is_locked: false,
          })
          .select()
          .single();

        if (createError) {
          logger.error('Error creating blueprint', { error: createError, projectId });
          throw createError;
        }

        blueprint = newBlueprint;
      }

      const blueprintContent = blueprint.content || {};
      const updatedContent: any = { ...blueprintContent };

      // Process each phase from analysis
      for (const phaseAnalysis of analysisResults.phases) {
        const phaseMapping = PHASE_MAPPING[phaseAnalysis.phaseName];
        if (!phaseMapping) {
          logger.warn('Unknown phase in analysis', {
            phaseName: phaseAnalysis.phaseName,
            projectId,
          });
          continue;
        }

        // Update blueprint content for this phase
        const phaseData = phaseAnalysis.mergedAnalysis;
        
        // For scope: store findings as requirements array and features array
        if (phaseMapping.blueprintSectionId === 'scope') {
          updatedContent[phaseMapping.blueprintSectionId] = {
            completeness: phaseData.overallCompleteness,
            findings: phaseData.combinedFindings,
            recommendations: phaseData.recommendations,
            detectedComponents: phaseData.detectedComponents,
            missingComponents: phaseData.missingComponents,
            lastAnalyzed: analysisResults.analyzedAt,
          };
          
          // Also populate requirements and features arrays if we have findings
          if (phaseData.combinedFindings && phaseData.combinedFindings.length > 0) {
            updatedContent.requirements = phaseData.combinedFindings.slice(0, 10);
            updatedContent.features = phaseData.detectedComponents.slice(0, 10);
          }
        }
        // For architecture: store patterns, components, layers
        else if (phaseMapping.blueprintSectionId === 'architecture') {
          updatedContent[phaseMapping.blueprintSectionId] = {
            completeness: phaseData.overallCompleteness,
            patterns: phaseData.detectedComponents.filter((c: string) => 
              c.toLowerCase().includes('pattern') || c.toLowerCase().includes('design')
            ).slice(0, 10),
            components: phaseData.detectedComponents.slice(0, 20),
            layers: phaseData.combinedFindings.filter((f: string) => 
              f.toLowerCase().includes('layer') || f.toLowerCase().includes('tier')
            ).slice(0, 10),
            findings: phaseData.combinedFindings,
            recommendations: phaseData.recommendations,
            lastAnalyzed: analysisResults.analyzedAt,
          };
        }
        // For other sections: store standard structure
        else {
          updatedContent[phaseMapping.blueprintSectionId] = {
            completeness: phaseData.overallCompleteness,
            findings: phaseData.combinedFindings,
            recommendations: phaseData.recommendations,
            detectedComponents: phaseData.detectedComponents,
            missingComponents: phaseData.missingComponents,
            architectInsights: phaseAnalysis.architectAnalysis,
            fullStackInsights: phaseAnalysis.fullStackAnalysis,
            lastAnalyzed: analysisResults.analyzedAt,
          };
        }

        // Sync to plan tree via sync service
        await this.syncBlueprintToPlanTree(
          projectId,
          phaseMapping.blueprintSectionId,
          updatedContent[phaseMapping.blueprintSectionId]
        );
      }

      // Update tech stack in blueprint
      if (analysisResults.techStack) {
        const techStackData = {
          frontend: analysisResults.techStack.frontend || [],
          backend: analysisResults.techStack.backend || [],
          database: analysisResults.techStack.database || [],
          tools: analysisResults.techStack.tools || [],
          infrastructure: analysisResults.techStack.tools || [],
        };
        
        updatedContent.techstack = techStackData;
        updatedContent.techStack = techStackData;

        // Sync tech stack
        await this.syncBlueprintToPlanTree(
          projectId,
          'techstack',
          techStackData
        );
      }

      // Update overall blueprint with analysis metadata
      updatedContent.agent_analysis = {
        analyzedAt: analysisResults.analyzedAt,
        overallCompletion: analysisResults.overallCompletion,
        phasesAnalyzed: analysisResults.phases.length,
      };

      // Save updated blueprint
      const { error: updateError } = await supabase
        .from('project_blueprints')
        .update({
          content: updatedContent,
          updated_at: new Date().toISOString(),
        })
        .eq('project_id', projectId);

      if (updateError) {
        logger.error('Error saving blueprint content after agent analysis', {
          error: updateError,
          projectId,
          contentKeys: Object.keys(updatedContent),
        });
        throw updateError;
      }

      logger.info('SDLC tree and blueprint populated from agent analysis', {
        projectId,
        phasesProcessed: analysisResults.phases.length,
        overallCompletion: analysisResults.overallCompletion,
        blueprintSections: Object.keys(updatedContent).length,
        hasTechStack: !!updatedContent.techstack || !!updatedContent.techStack,
      });
    } catch (error) {
      logger.error('Error populating SDLC tree from analysis', { error, projectId });
      throw error;
    }
  }

  static async updatePhaseFromAnalysis(
    projectId: string,
    phaseName: string,
    phaseAnalysis: any
  ): Promise<void> {
    try {
      const phaseMapping = PHASE_MAPPING[phaseName];
      if (!phaseMapping) {
        logger.warn('Unknown phase', { phaseName, projectId });
        return;
      }

      // Update blueprint
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('content')
        .eq('project_id', projectId)
        .maybeSingle();

      if (blueprint) {
        const content = blueprint.content || {};
        content[phaseMapping.blueprintSectionId] = {
          completeness: phaseAnalysis.mergedAnalysis?.overallCompleteness || 0,
          findings: phaseAnalysis.mergedAnalysis?.combinedFindings || [],
          recommendations: phaseAnalysis.mergedAnalysis?.recommendations || [],
          lastAnalyzed: new Date().toISOString(),
        };

        await supabase
          .from('project_blueprints')
          .update({
            content,
            updated_at: new Date().toISOString(),
          })
          .eq('project_id', projectId);

        // Sync to plan tree
        await this.syncBlueprintToPlanTree(
          projectId,
          phaseMapping.blueprintSectionId,
          content[phaseMapping.blueprintSectionId]
        );
      }
    } catch (error) {
      logger.error('Error updating phase from analysis', { error, projectId, phaseName });
    }
  }

  static async getPhaseCompletion(
    projectId: string,
    phaseName: string
  ): Promise<number> {
    try {
      const phaseMapping = PHASE_MAPPING[phaseName];
      if (!phaseMapping) return 0;

      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('content')
        .eq('project_id', projectId)
        .maybeSingle();

      if (blueprint?.content?.[phaseMapping.blueprintSectionId]) {
        return (
          blueprint.content[phaseMapping.blueprintSectionId].completeness || 0
        );
      }

      return 0;
    } catch (error) {
      logger.error('Error getting phase completion', { error, projectId, phaseName });
      return 0;
    }
  }

  // ==========================================================================
  // Private Helper Methods
  // ==========================================================================

  private static async checkAdminPermissions(userId: string): Promise<boolean> {
    try {
      const { data: user, error } = await supabase
        .from('users')
        .select('role')
        .eq('id', userId)
        .single();

      if (error || !user) {
        return false;
      }

      return user.role === 'admin' || user.role === 'super_admin';
    } catch (error) {
      logger.error('Error checking admin permissions', { error, userId });
      return false;
    }
  }

  private static async logLockEvent(
    blueprintId: string,
    userId: string,
    action: 'lock' | 'unlock',
    reason: string
  ): Promise<void> {
    try {
      await supabase.from('blueprint_lock_events').insert({
        blueprint_id: blueprintId,
        user_id: userId,
        action,
        reason,
        timestamp: new Date().toISOString(),
      });

      logger.info('Blueprint lock event logged', { blueprintId, action });
    } catch (error) {
      logger.error('Error logging lock event', { error, blueprintId });
    }
  }

  private static async logModificationAttempt(
    blueprintId: string,
    userId: string,
    modificationType: 'edit' | 'delete' | 'unlock',
    denied: boolean,
    reason: string
  ): Promise<void> {
    try {
      await supabase.from('blueprint_modification_attempts').insert({
        blueprint_id: blueprintId,
        user_id: userId,
        modification_type: modificationType,
        denied,
        reason,
        attempted_at: new Date().toISOString(),
      });

      logger.info('Blueprint modification attempt logged', {
        blueprintId,
        modificationType,
        denied,
      });
    } catch (error) {
      logger.error('Error logging modification attempt', { error, blueprintId });
    }
  }

  private static calculateBlueprintCompletion(content: any): number {
    if (!content) return 0;

    const sections = [
      'overview',
      'scope',
      'architecture',
      'techstack',
      'wireframes',
      'schema',
      'stories',
      'standards',
      'theme',
      'timeline',
    ];

    let completed = 0;
    for (const section of sections) {
      const sectionData = content[section];
      if (sectionData) {
        // Check if section has meaningful data
        if (Array.isArray(sectionData) && sectionData.length > 0) {
          completed++;
        } else if (typeof sectionData === 'object' && Object.keys(sectionData).length > 0) {
          completed++;
        } else if (typeof sectionData === 'string' && sectionData.trim().length > 0) {
          completed++;
        }
      }
    }

    return Math.round((completed / sections.length) * 100);
  }

  private static validateStructure(
    blueprint: any,
    errors: ValidationError[],
    warnings: ValidationWarning[]
  ): void {
    if (!blueprint.title || blueprint.title.trim().length === 0) {
      errors.push({
        field: 'title',
        message: 'Title is required',
        severity: 'critical',
      });
    }

    if (!blueprint.description || blueprint.description.trim().length < 20) {
      errors.push({
        field: 'description',
        message: 'Description must be at least 20 characters',
        severity: 'error',
      });
    }

    if (!blueprint.architecture || typeof blueprint.architecture !== 'object') {
      errors.push({
        field: 'architecture',
        message: 'Architecture section is required',
        severity: 'critical',
      });
      return;
    }

    const requiredArchFields = ['layers', 'patterns', 'components'];
    requiredArchFields.forEach((field) => {
      if (!Array.isArray(blueprint.architecture[field])) {
        errors.push({
          field: `architecture.${field}`,
          message: `Architecture ${field} must be an array`,
          severity: 'error',
        });
      } else if (blueprint.architecture[field].length === 0) {
        warnings.push({
          field: `architecture.${field}`,
          message: `Architecture ${field} is empty`,
          severity: 'warning',
        });
      }
    });

    if (!blueprint.techStack || typeof blueprint.techStack !== 'object') {
      errors.push({
        field: 'techStack',
        message: 'Tech stack section is required',
        severity: 'critical',
      });
      return;
    }

    const requiredTechFields = ['frontend', 'backend', 'database'];
    requiredTechFields.forEach((field) => {
      if (!Array.isArray(blueprint.techStack[field])) {
        errors.push({
          field: `techStack.${field}`,
          message: `Tech stack ${field} must be an array`,
          severity: 'error',
        });
      } else if (blueprint.techStack[field].length === 0) {
        warnings.push({
          field: `techStack.${field}`,
          message: `Tech stack ${field} is empty`,
          severity: 'warning',
        });
      }
    });

    if (!Array.isArray(blueprint.phases) || blueprint.phases.length === 0) {
      errors.push({
        field: 'phases',
        message: 'At least one phase is required',
        severity: 'critical',
      });
    } else {
      blueprint.phases.forEach((phase: any, index: number) => {
        if (!phase.name) {
          errors.push({
            field: `phases[${index}].name`,
            message: `Phase ${index + 1} must have a name`,
            severity: 'error',
          });
        }
        if (!phase.description) {
          warnings.push({
            field: `phases[${index}].description`,
            message: `Phase ${index + 1} should have a description`,
            severity: 'warning',
          });
        }
        if (!Array.isArray(phase.deliverables) || phase.deliverables.length === 0) {
          warnings.push({
            field: `phases[${index}].deliverables`,
            message: `Phase ${index + 1} should have deliverables`,
            severity: 'warning',
          });
        }
      });
    }
  }

  private static validateCompleteness(
    blueprint: any,
    _errors: ValidationError[],
    warnings: ValidationWarning[],
    suggestions: string[]
  ): void {
    if (blueprint.title && blueprint.title.length < 10) {
      warnings.push({
        field: 'title',
        message: 'Title is quite short, consider adding more detail',
        severity: 'info',
      });
    }

    if (blueprint.description && blueprint.description.length < 100) {
      suggestions.push('Consider expanding the description to provide more context');
    }

    if (blueprint.architecture?.patterns?.length === 0) {
      suggestions.push('Add architectural patterns (e.g., MVC, Microservices, Event-Driven)');
    }

    if (blueprint.standards && (!blueprint.standards.coding || blueprint.standards.coding.length === 0)) {
      suggestions.push('Define coding standards for consistency across the team');
    }

    if (blueprint.standards && (!blueprint.standards.testing || blueprint.standards.testing.length === 0)) {
      suggestions.push('Specify testing standards and requirements');
    }

    if (blueprint.phases) {
      const hasDependencies = blueprint.phases.some((p: any) =>
        Array.isArray(p.dependencies) && p.dependencies.length > 0
      );
      if (!hasDependencies) {
        suggestions.push('Consider adding phase dependencies to clarify execution order');
      }
    }
  }

  private static validateConsistency(
    blueprint: any,
    _errors: ValidationError[],
    warnings: ValidationWarning[]
  ): void {
    const frontendTech = blueprint.techStack?.frontend || [];
    const backendTech = blueprint.techStack?.backend || [];
    const database = blueprint.techStack?.database || [];

    if (frontendTech.includes('React') && backendTech.includes('Django')) {
      warnings.push({
        field: 'techStack',
        message: 'React with Django requires CORS configuration',
        severity: 'info',
      });
    }

    if (database.includes('MongoDB') && database.includes('PostgreSQL')) {
      warnings.push({
        field: 'techStack.database',
        message: 'Using both MongoDB and PostgreSQL may increase complexity',
        severity: 'warning',
      });
    }

    if (frontendTech.includes('TypeScript') && !backendTech.includes('TypeScript') && !backendTech.includes('Node.js')) {
      warnings.push({
        field: 'techStack',
        message: 'Consider using TypeScript on backend for type consistency',
        severity: 'info',
      });
    }
  }

  private static calculateValidationScore(
    errors: ValidationError[],
    warnings: ValidationWarning[]
  ): number {
    let score = 100;

    errors.forEach((error) => {
      score -= error.severity === 'critical' ? 20 : 10;
    });

    warnings.forEach((warning) => {
      score -= warning.severity === 'warning' ? 5 : 2;
    });

    return Math.max(0, score);
  }

  private static buildAIValidationPrompt(blueprint: any): string {
    return `You are an expert software architect. Review this project blueprint and provide detailed feedback.

BLUEPRINT:
Title: ${blueprint.title}
Description: ${blueprint.description}

Architecture:
- Layers: ${blueprint.architecture?.layers?.join(', ') || 'None specified'}
- Patterns: ${blueprint.architecture?.patterns?.join(', ') || 'None specified'}
- Components: ${blueprint.architecture?.components?.join(', ') || 'None specified'}

Tech Stack:
- Frontend: ${blueprint.techStack?.frontend?.join(', ') || 'None specified'}
- Backend: ${blueprint.techStack?.backend?.join(', ') || 'None specified'}
- Database: ${blueprint.techStack?.database?.join(', ') || 'None specified'}
- Infrastructure: ${blueprint.techStack?.infrastructure?.join(', ') || 'None specified'}

Phases: ${blueprint.phases?.length || 0} phases defined

Please provide your review in this JSON format:
{
  "summary": "2-3 sentence overall assessment",
  "strengths": ["strength 1", "strength 2", "strength 3"],
  "weaknesses": ["weakness 1", "weakness 2"],
  "improvements": ["specific improvement 1", "specific improvement 2", "specific improvement 3"]
}

Focus on:
1. Architecture soundness and scalability
2. Tech stack compatibility and modern best practices
3. Completeness and feasibility
4. Potential risks or bottlenecks
5. Missing critical components`;
  }

  private static parseAIValidationResponse(response: string): {
    summary: string;
    strengths: string[];
    weaknesses: string[];
    improvements: string[];
  } {
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          summary: parsed.summary || 'No summary provided',
          strengths: parsed.strengths || [],
          weaknesses: parsed.weaknesses || [],
          improvements: parsed.improvements || [],
        };
      }
    } catch (error) {
      logger.error('Failed to parse AI response:', error);
    }

    return {
      summary: 'AI validation completed but response format was invalid',
      strengths: [],
      weaknesses: [],
      improvements: [],
    };
  }

  private static async saveValidationResult(
    blueprintId: string,
    result: ValidationResult
  ): Promise<void> {
    try {
      await supabase.from('blueprint_validations').insert({
        blueprint_id: blueprintId,
        is_valid: result.isValid,
        score: result.score,
        errors: result.errors,
        warnings: result.warnings,
        suggestions: result.suggestions,
        ai_review: result.aiReview,
        validated_at: new Date().toISOString(),
      });
    } catch (error) {
      logger.error('Failed to save validation result:', error);
    }
  }

  private static async getProjectDetails(projectId: string): Promise<any> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (error) throw error;
    return data;
  }

  private static buildRequirementsSection(
    atomicRequirements: any[],
    userStories: any[],
    stakeholders: any[]
  ): BlueprintTreeNode {
    return {
      id: 'requirements',
      type: 'section',
      label: 'Requirements',
      icon: '🎯',
      count: atomicRequirements.length + userStories.length + stakeholders.length,
      status: this.calculateSectionStatus(atomicRequirements, userStories, stakeholders),
      children: [
        {
          id: 'stakeholders',
          type: 'item',
          label: 'Stakeholders',
          icon: '👥',
          count: stakeholders.length,
          status: this.calculateItemStatus(stakeholders),
          children: stakeholders.map(stakeholder => ({
            id: `stakeholder-${stakeholder.id}`,
            type: 'subitem',
            label: stakeholder.name,
            icon: '👤',
            data: stakeholder,
            lastUpdated: stakeholder.updated_at
          }))
        },
        {
          id: 'user-stories',
          type: 'item',
          label: 'User Stories',
          icon: '📖',
          count: userStories.length,
          status: this.calculateItemStatus(userStories),
          children: userStories.map(story => ({
            id: `story-${story.id}`,
            type: 'subitem',
            label: story.title,
            icon: '📝',
            data: story,
            lastUpdated: story.updated_at
          }))
        },
        {
          id: 'atomic-requirements',
          type: 'item',
          label: 'Atomic Requirements',
          icon: '⚡',
          count: atomicRequirements.length,
          status: this.calculateItemStatus(atomicRequirements),
          children: atomicRequirements.map(req => ({
            id: `req-${req.id}`,
            type: 'subitem',
            label: req.name,
            icon: '🔧',
            data: req,
            lastUpdated: req.updated_at
          }))
        }
      ]
    };
  }

  private static buildArchitectureSection(project: any): BlueprintTreeNode {
    return {
      id: 'architecture',
      type: 'section',
      label: 'Architecture',
      icon: '🏗️',
      status: project.architecture_defined ? 'complete' : 'pending',
      children: [
        {
          id: 'tech-stack',
          type: 'item',
          label: 'Tech Stack',
          icon: '⚙️',
          data: project.tech_stack || {},
          status: project.tech_stack ? 'complete' : 'pending'
        },
        {
          id: 'system-design',
          type: 'item',
          label: 'System Design',
          icon: '🏛️',
          data: project.system_design || {},
          status: project.system_design ? 'complete' : 'pending'
        },
        {
          id: 'database-schema',
          type: 'item',
          label: 'Database Schema',
          icon: '🗄️',
          data: project.database_schema || {},
          status: project.database_schema ? 'complete' : 'pending'
        }
      ]
    };
  }

  private static buildConventionsSection(project: any): BlueprintTreeNode {
    return {
      id: 'conventions',
      type: 'section',
      label: 'Conventions',
      icon: '📐',
      status: project.conventions_defined ? 'complete' : 'pending',
      children: [
        {
          id: 'coding-standards',
          type: 'item',
          label: 'Coding Standards',
          icon: '💻',
          data: project.coding_standards || {},
          status: project.coding_standards ? 'complete' : 'pending'
        },
        {
          id: 'naming-conventions',
          type: 'item',
          label: 'Naming Conventions',
          icon: '🏷️',
          data: project.naming_conventions || {},
          status: project.naming_conventions ? 'complete' : 'pending'
        },
        {
          id: 'git-workflow',
          type: 'item',
          label: 'Git Workflow',
          icon: '🌿',
          data: project.git_workflow || {},
          status: project.git_workflow ? 'complete' : 'pending'
        }
      ]
    };
  }

  private static buildSecuritySection(project: any): BlueprintTreeNode {
    return {
      id: 'security',
      type: 'section',
      label: 'Security',
      icon: '🔒',
      status: project.security_standards ? 'complete' : 'pending',
      children: [
        {
          id: 'authentication',
          type: 'item',
          label: 'Authentication',
          icon: '🔑',
          data: project.auth_requirements || {},
          status: project.auth_requirements ? 'complete' : 'pending'
        },
        {
          id: 'authorization',
          type: 'item',
          label: 'Authorization',
          icon: '🛡️',
          data: project.authorization_requirements || {},
          status: project.authorization_requirements ? 'complete' : 'pending'
        },
        {
          id: 'data-protection',
          type: 'item',
          label: 'Data Protection',
          icon: '🔐',
          data: project.data_protection_requirements || {},
          status: project.data_protection_requirements ? 'complete' : 'pending'
        }
      ]
    };
  }

  private static buildDesignSection(wireframes: any[], project: any): BlueprintTreeNode {
    return {
      id: 'design',
      type: 'section',
      label: 'Design System',
      icon: '🎨',
      count: wireframes.length,
      status: this.calculateItemStatus(wireframes),
      children: [
        {
          id: 'wireframes',
          type: 'item',
          label: 'Wireframes',
          icon: '📐',
          count: wireframes.length,
          status: this.calculateItemStatus(wireframes),
          children: wireframes.map(wireframe => ({
            id: `wireframe-${wireframe.id}`,
            type: 'subitem',
            label: wireframe.name,
            icon: '🖼️',
            data: wireframe,
            lastUpdated: wireframe.updated_at
          }))
        },
        {
          id: 'theme',
          type: 'item',
          label: 'Theme',
          icon: '🎨',
          data: project.theme || {},
          status: project.theme ? 'complete' : 'pending'
        },
        {
          id: 'assets',
          type: 'item',
          label: 'Assets',
          icon: '🖼️',
          data: project.assets || {},
          status: project.assets ? 'complete' : 'pending'
        }
      ]
    };
  }

  private static buildPhasesSection(project: any): BlueprintTreeNode {
    return {
      id: 'phases',
      type: 'section',
      label: 'Phases',
      icon: '🚀',
      status: project.phases_defined ? 'complete' : 'pending',
      children: [
        {
          id: 'planning',
          type: 'item',
          label: 'Planning',
          icon: '📋',
          status: 'complete'
        },
        {
          id: 'development',
          type: 'item',
          label: 'Development',
          icon: '💻',
          status: 'pending'
        },
        {
          id: 'testing',
          type: 'item',
          label: 'Testing',
          icon: '🧪',
          status: 'pending'
        },
        {
          id: 'deployment',
          type: 'item',
          label: 'Deployment',
          icon: '🚀',
          status: 'pending'
        }
      ]
    };
  }

  private static calculateSectionStatus(...items: any[][]): 'complete' | 'in_progress' | 'pending' | 'needs_review' {
    const totalItems = items.reduce((sum, arr) => sum + arr.length, 0);
    if (totalItems === 0) return 'pending';
    
    const completedItems = items.reduce((sum, arr) => 
      sum + arr.filter(item => item.status === 'complete' || item.status === 'approved').length, 0
    );
    
    const completionRate = completedItems / totalItems;
    if (completionRate === 1) return 'complete';
    if (completionRate >= 0.5) return 'in_progress';
    return 'pending';
  }

  private static calculateItemStatus(items: any[]): 'complete' | 'in_progress' | 'pending' | 'needs_review' {
    if (items.length === 0) return 'pending';
    
    const completedItems = items.filter(item => 
      item.status === 'complete' || item.status === 'approved'
    ).length;
    
    const completionRate = completedItems / items.length;
    if (completionRate === 1) return 'complete';
    if (completionRate >= 0.5) return 'in_progress';
    return 'pending';
  }

  private static async loadNodeData(nodeId: string, projectId: string): Promise<[BlueprintTreeNode, any]> {
    try {
      // Load the full blueprint from the database
      const { data: blueprint, error } = await supabase
        .from('blueprints')
        .select('*')
        .eq('project_id', projectId)
        .single();

      if (error || !blueprint) {
        logger.error('Failed to load blueprint for node data', { error, projectId, nodeId });
        throw new Error('Blueprint not found');
      }

      // Parse the node path from nodeId (format: section_name or section_name.item_name)
      const pathParts = nodeId.split('.');
      const sectionKey = pathParts[0];
      
      let node: BlueprintTreeNode;
      let data: any = {};

      // Get the section data from blueprint content
      const sectionMap: Record<string, { label: string; icon: string; dataKey: keyof typeof blueprint }> = {
        'overview': { label: 'Overview', icon: '📋', dataKey: 'overview' as keyof typeof blueprint },
        'scope': { label: 'Scope & Requirements', icon: '🎯', dataKey: 'scope' as keyof typeof blueprint },
        'architecture': { label: 'Architecture', icon: '🏗️', dataKey: 'architecture' as keyof typeof blueprint },
        'tech_stack': { label: 'Tech Stack', icon: '⚙️', dataKey: 'tech_stack' as keyof typeof blueprint },
        'wireframes': { label: 'Wireframes', icon: '📐', dataKey: 'wireframes_json' as keyof typeof blueprint },
        'database': { label: 'Database Schema', icon: '🗄️', dataKey: 'database_schema' as keyof typeof blueprint },
        'user_stories': { label: 'User Stories', icon: '👤', dataKey: 'user_stories' as keyof typeof blueprint },
        'standards': { label: 'Standards', icon: '📏', dataKey: 'standards' as keyof typeof blueprint }
      };

      const section = sectionMap[sectionKey];
      if (section) {
        node = {
          id: nodeId,
          type: pathParts.length === 1 ? 'section' : 'item',
          label: pathParts.length === 1 ? section.label : pathParts[pathParts.length - 1].replace(/_/g, ' '),
          icon: section.icon
        };

        // Load the actual data for this section
        data = blueprint[section.dataKey] || {};
      } else {
        // Fallback for unknown sections
        node = {
          id: nodeId,
          type: 'item',
          label: pathParts[pathParts.length - 1].replace(/_/g, ' '),
          icon: '📝'
        };
      }

      return [node, data];
    } catch (error) {
      logger.error('Error loading node data', { error, projectId, nodeId });
      // Return minimal structure on error
      return [
        {
          id: nodeId,
          type: 'item',
          label: nodeId.split('.').pop()?.replace(/_/g, ' ') || 'Unknown',
          icon: '📝'
        },
        {}
      ];
    }
  }

  private static generatePromptContext(node: BlueprintTreeNode, data: any): string {
    return `You are working on: ${node.label}\n\nContext: ${JSON.stringify(data, null, 2)}`;
  }

  private static generatePromptSuggestions(node: BlueprintTreeNode, data: any): string[] {
    const suggestions: string[] = [];
    
    switch (node.type) {
      case 'section':
        suggestions.push(`Define the ${node.label.toLowerCase()} for this project`);
        suggestions.push(`What are the key components of ${node.label.toLowerCase()}?`);
        break;
      case 'item':
        suggestions.push(`Expand on ${node.label.toLowerCase()}`);
        suggestions.push(`Add more details to ${node.label.toLowerCase()}`);
        break;
      case 'subitem':
        suggestions.push(`Refine ${node.label.toLowerCase()}`);
        suggestions.push(`Add more context to ${node.label.toLowerCase()}`);
        break;
    }

    return suggestions;
  }

  private static async processPromptResponse(
    nodeId: string,
    projectId: string,
    prompt: string,
    response: string
  ): Promise<{
    success: boolean;
    updatedData: any;
    newChildren?: BlueprintTreeNode[];
  }> {
    try {
      // Load current blueprint
      const { data: blueprint, error } = await supabase
        .from('blueprints')
        .select('*')
        .eq('project_id', projectId)
        .single();

      if (error || !blueprint) {
        logger.error('Failed to load blueprint for update', { error, projectId, nodeId });
        return { success: false, updatedData: {}, newChildren: [] };
      }

      // Parse the AI response and node path
      const pathParts = nodeId.split('.');
      const sectionKey = pathParts[0];

      // Map section keys to database columns
      const sectionToColumn: Record<string, string> = {
        'overview': 'overview',
        'scope': 'scope',
        'architecture': 'architecture',
        'tech_stack': 'tech_stack',
        'wireframes': 'wireframes_json',
        'database': 'database_schema',
        'user_stories': 'user_stories',
        'standards': 'standards'
      };

      const columnName = sectionToColumn[sectionKey];
      if (!columnName) {
        logger.warn('Unknown section for update', { nodeId, sectionKey });
        return { success: false, updatedData: {}, newChildren: [] };
      }

      // Get current section data
      const currentData = blueprint[columnName as keyof typeof blueprint] || {};
      
      // Update the specific field with the AI response
      const updatedData = {
        ...currentData,
        [pathParts[pathParts.length - 1]]: response,
        last_refined: new Date().toISOString(),
        refined_by_prompt: prompt
      };

      // Update the blueprint in the database
      const { error: updateError } = await supabase
        .from('blueprints')
        .update({
          [columnName]: updatedData,
          updated_at: new Date().toISOString()
        })
        .eq('project_id', projectId);

      if (updateError) {
        logger.error('Failed to update blueprint', { error: updateError, projectId, nodeId });
        return { success: false, updatedData: {}, newChildren: [] };
      }

      logger.info('Blueprint section updated successfully', { projectId, nodeId, sectionKey });

      return {
        success: true,
        updatedData,
        newChildren: []
      };
    } catch (error) {
      logger.error('Error processing prompt response', { error, projectId, nodeId });
      return { success: false, updatedData: {}, newChildren: [] };
    }
  }

  private static async updateTreeStructure(
    nodeId: string,
    projectId: string,
    newChildren: BlueprintTreeNode[]
  ): Promise<void> {
    // This would update the tree structure in the database
    logger.info('Updating tree structure', { nodeId, projectId, newChildrenCount: newChildren.length });
  }

  private static searchTreeRecursive(
    node: BlueprintTreeNode,
    query: string,
    results: BlueprintTreeNode[]
  ): void {
    if (node.label.toLowerCase().includes(query)) {
      results.push(node);
    }

    if (node.children) {
      node.children.forEach(child => this.searchTreeRecursive(child, query, results));
    }
  }

  private static async detectSchemaBackendGaps(tree: ArchitectureTree): Promise<GapAnalysisResult[]> {
    const gaps: GapAnalysisResult[] = [];
    const schemaNodes = tree.layers.schema;
    const backendNodes = tree.layers.backend;

    for (const schemaNode of schemaNodes) {
      if (schemaNode.type !== 'table') continue;

      const tableName = schemaNode.name;
      
      // Check if there's a backend service or API for this table
      const hasBackendService = backendNodes.some(backendNode =>
        backendNode.name.toLowerCase().includes(tableName.toLowerCase()) ||
        tree.connections.some(conn =>
          conn.sourceId === schemaNode.id && conn.targetId === backendNode.id
        )
      );

      if (!hasBackendService) {
        gaps.push({
          id: `gap-schema-backend-${tableName}`,
          type: 'missing-connection',
          severity: 'high',
          layer: 'schema-backend',
          sourceNode: schemaNode,
          description: `Database table "${tableName}" has no corresponding backend service or API`,
          recommendation: `Create a service or API endpoint to handle CRUD operations for the "${tableName}" table`,
          autoFixAvailable: true,
        });
      }
    }

    return gaps;
  }

  private static async detectBackendFrontendGaps(tree: ArchitectureTree): Promise<GapAnalysisResult[]> {
    const gaps: GapAnalysisResult[] = [];
    const backendNodes = tree.layers.backend.filter(n => n.type === 'api');
    const frontendNodes = tree.layers.frontend;

    for (const backendNode of backendNodes) {
      // Check if any frontend component calls this API
      const isCalledByFrontend = tree.connections.some(
        conn =>
          conn.sourceId === backendNode.id &&
          frontendNodes.some(fn => fn.id === conn.targetId)
      );

      if (!isCalledByFrontend) {
        gaps.push({
          id: `gap-backend-frontend-${backendNode.name}`,
          type: 'unused-resource',
          severity: 'medium',
          layer: 'backend-frontend',
          sourceNode: backendNode,
          description: `Backend API "${backendNode.name}" is not called by any frontend component`,
          recommendation: `Either implement frontend components that use this API, or remove it if it's not needed`,
          autoFixAvailable: false,
        });
      }
    }

    // Check for frontend components making API calls without backend endpoints
    for (const frontendNode of frontendNodes) {
      const apiCalls = frontendNode.metadata.apiCalls || [];
      for (const apiCall of apiCalls) {
        const hasBackendEndpoint = backendNodes.some(bn =>
          bn.name.toLowerCase().includes(apiCall.toLowerCase()) ||
          bn.metadata.filePath?.includes(apiCall)
        );

        if (!hasBackendEndpoint) {
          gaps.push({
            id: `gap-frontend-backend-${frontendNode.name}-${apiCall}`,
            type: 'missing-implementation',
            severity: 'critical',
            layer: 'frontend-backend',
            sourceNode: frontendNode,
            description: `Frontend component "${frontendNode.name}" calls API "${apiCall}" which doesn't exist in backend`,
            recommendation: `Implement the "${apiCall}" API endpoint in the backend`,
            autoFixAvailable: true,
          });
        }
      }
    }

    return gaps;
  }

  private static async detectUnusedResources(tree: ArchitectureTree): Promise<GapAnalysisResult[]> {
    const gaps: GapAnalysisResult[] = [];
    const allNodes = tree.nodes;

    for (const node of allNodes) {
      // Check if node has any connections (incoming or outgoing)
      const hasConnections = tree.connections.some(
        conn => conn.sourceId === node.id || conn.targetId === node.id
      );

      // Skip schema columns and certain node types
      if (node.type === 'column' || node.type === 'businessLogic') continue;

      if (!hasConnections && node.type !== 'route') {
        gaps.push({
          id: `gap-unused-${node.id}`,
          type: 'unused-resource',
          severity: 'low',
          layer: node.layer,
          sourceNode: node,
          description: `${node.layer} ${node.type} "${node.name}" appears to be unused (no connections detected)`,
          recommendation: `Review if "${node.name}" is still needed, or ensure proper connections are established`,
          autoFixAvailable: false,
        });
      }
    }

    return gaps;
  }

  private static async detectMissingImplementations(tree: ArchitectureTree): Promise<GapAnalysisResult[]> {
    const gaps: GapAnalysisResult[] = [];

    // Check for foreign key references without corresponding tables
    const schemaNodes = tree.layers.schema;
    for (const schemaNode of schemaNodes) {
      if (schemaNode.children) {
        for (const column of schemaNode.children) {
          if (column.metadata.foreignKey) {
            const foreignTable = column.metadata.foreignKey.table;
            const hasForeignTable = schemaNodes.some(
              n => n.type === 'table' && n.name === foreignTable
            );

            if (!hasForeignTable) {
              gaps.push({
                id: `gap-missing-table-${foreignTable}`,
                type: 'missing-implementation',
                severity: 'critical',
                layer: 'schema',
                sourceNode: schemaNode,
                description: `Table "${schemaNode.name}" references foreign table "${foreignTable}" which doesn't exist`,
                recommendation: `Create the "${foreignTable}" table or remove the foreign key reference`,
                autoFixAvailable: true,
              });
            }
          }
        }
      }
    }

    return gaps;
  }

  private static async detectOrphanedNodes(tree: ArchitectureTree): Promise<GapAnalysisResult[]> {
    const gaps: GapAnalysisResult[] = [];

    // Check for frontend routes without components
    const routes = tree.layers.frontend.filter(n => n.type === 'route');
    const components = tree.layers.frontend.filter(n => n.type === 'component');

    for (const route of routes) {
      const routeName = route.name;
      const hasComponent = components.some(c =>
        c.name.toLowerCase().includes(routeName.toLowerCase()) ||
        tree.connections.some(conn =>
          conn.sourceId === route.id && conn.targetId === c.id
        )
      );

      if (!hasComponent) {
        gaps.push({
          id: `gap-orphaned-route-${routeName}`,
          type: 'missing-implementation',
          severity: 'high',
          layer: 'frontend',
          sourceNode: route,
          description: `Route "${routeName}" has no associated component`,
          recommendation: `Create a component for the "${routeName}" route`,
          autoFixAvailable: true,
        });
      }
    }

    return gaps;
  }

  private static calculateGapSummary(gaps: GapAnalysisResult[]) {
    const summary = {
      total: gaps.length,
      critical: gaps.filter(g => g.severity === 'critical').length,
      high: gaps.filter(g => g.severity === 'high').length,
      medium: gaps.filter(g => g.severity === 'medium').length,
      low: gaps.filter(g => g.severity === 'low').length,
      byType: {} as Record<string, number>,
    };

    // Count by type
    for (const gap of gaps) {
      summary.byType[gap.type] = (summary.byType[gap.type] || 0) + 1;
    }

    return summary;
  }

  private static calculateIntegrationScore(
    tree: ArchitectureTree,
    gaps: GapAnalysisResult[]
  ): number {
    const totalNodes = tree.nodes.length;
    const totalConnections = tree.connections.length;
    const totalGaps = gaps.length;

    // Weight gaps by severity
    const weightedGaps =
      gaps.filter(g => g.severity === 'critical').length * 10 +
      gaps.filter(g => g.severity === 'high').length * 5 +
      gaps.filter(g => g.severity === 'medium').length * 2 +
      gaps.filter(g => g.severity === 'low').length * 1;

    // Expected connections (assume each node should have at least 1 connection)
    const expectedConnections = totalNodes;
    const connectionScore = Math.min(100, (totalConnections / expectedConnections) * 100);

    // Gap penalty (reduce score based on weighted gaps)
    const maxPenalty = 50; // Maximum 50 point deduction
    const gapPenalty = Math.min(maxPenalty, weightedGaps * 2);

    // Final score
    const score = Math.max(0, Math.min(100, connectionScore - gapPenalty));

    return Math.round(score);
  }

  private static generateGapRecommendations(
    gaps: GapAnalysisResult[],
    tree: ArchitectureTree
  ): string[] {
    const recommendations: string[] = [];

    // Critical gaps first
    const criticalGaps = gaps.filter(g => g.severity === 'critical');
    if (criticalGaps.length > 0) {
      recommendations.push(
        `⚠️ CRITICAL: Address ${criticalGaps.length} critical gap(s) before locking blueprint`
      );
    }

    // Schema-backend gaps
    const schemaGaps = gaps.filter(g => g.layer.includes('schema'));
    if (schemaGaps.length > 0) {
      recommendations.push(
        `🗄️ Database: Create backend services for ${schemaGaps.length} table(s) without API coverage`
      );
    }

    // Backend-frontend gaps
    const apiGaps = gaps.filter(g => g.layer.includes('backend-frontend'));
    if (apiGaps.length > 0) {
      recommendations.push(
        `🔌 API Integration: ${apiGaps.length} missing connection(s) between backend and frontend`
      );
    }

    // Unused resources
    const unusedGaps = gaps.filter(g => g.type === 'unused-resource');
    if (unusedGaps.length > 5) {
      recommendations.push(
        `🧹 Cleanup: ${unusedGaps.length} unused resource(s) detected - consider removing to reduce complexity`
      );
    }

    // Overall health
    if (gaps.length === 0) {
      recommendations.push('✅ Excellent! No gaps detected - architecture is well-integrated');
    } else if (gaps.length < 5) {
      recommendations.push('✅ Good! Minor gaps detected - architecture is mostly well-integrated');
    } else if (gaps.length < 15) {
      recommendations.push('⚠️ Moderate gaps detected - review and address before proceeding');
    } else {
      recommendations.push('❌ Significant gaps detected - comprehensive review required');
    }

    return recommendations;
  }

  private static async saveGapAnalysisReport(
    projectId: string,
    report: GapAnalysisReport
  ): Promise<void> {
    try {
      // Store gap analysis in analysis_metadata JSONB column
      const { error } = await supabase
        .from('project_analysis')
        .upsert({
          project_id: projectId,
          status: 'completed',
          analysis_metadata: {
            gap_analysis: report,
            analysis_type: 'gap_analysis',
            analyzed_at: report.analyzedAt,
          },
          analyzed_at: report.analyzedAt,
        }, {
          onConflict: 'project_id',
        });

      if (error) throw error;

      logger.info('Gap analysis report saved', { projectId });
    } catch (error) {
      logger.error('Error saving gap analysis report', { error, projectId });
      // Don't throw - allow gap analysis to continue even if saving fails
    }
  }

  /**
   * Validate files against blueprint standards
   */
  static async validateAgainstStandards(
    projectId: string,
    filesGenerated: Array<{ path: string; contentHash: string; purpose: string }>
  ): Promise<{ compliant: boolean; violations: string[]; score: number }> {
    try {
      // Get project blueprint
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('content, standards')
        .eq('project_id', projectId)
        .maybeSingle();

      if (!blueprint) {
        return { compliant: true, violations: [], score: 100 }; // No blueprint to validate against
      }

      const violations: string[] = [];
      const standards = blueprint.standards || blueprint.content?.standards || {};

      // Check coding standards if defined
      if (standards.codingStandards) {
        // Basic validation - could be enhanced with actual code analysis
        for (const file of filesGenerated) {
          // Check if file purpose matches expected patterns
          if (standards.codingStandards.requiredPatterns) {
            const matchesPattern = standards.codingStandards.requiredPatterns.some(
              (pattern: string) => file.path.includes(pattern)
            );
            if (!matchesPattern && standards.codingStandards.requiredPatterns.length > 0) {
              violations.push(`File ${file.path} does not match required patterns`);
            }
          }
        }
      }

      // Check naming conventions if defined
      if (standards.namingConventions) {
        for (const file of filesGenerated) {
          const fileName = file.path.split('/').pop() || '';
          // Basic naming check - could be enhanced
          if (standards.namingConventions.case === 'camelCase' && fileName.includes('_')) {
            violations.push(`File ${file.path} violates camelCase naming convention`);
          }
        }
      }

      const score = violations.length === 0 ? 100 : Math.max(0, 100 - (violations.length * 10));

      return {
        compliant: violations.length === 0,
        violations,
        score
      };
    } catch (error) {
      logger.error('Error validating against standards', { error, projectId });
      return { compliant: false, violations: ['Validation failed'], score: 0 };
    }
  }

  // Placeholder for existing validation - implement as needed
  private static async existingValidationLogic(blueprint: Blueprint): Promise<any[]> {
    // Your existing validation checks here
    return [
      { check: 'structure', passed: true },
      { check: 'requirements', passed: true },
    ];
  }

  /**
   * Store blueprint with validation enforcement
   */
  private static async storeBlueprint(
    projectId: string,
    userId: string,
    blueprintData: any,
    metadata?: { validation?: ValidationResult }
  ): Promise<Blueprint> {
    try {
      // Sanitize content before storage
      const sanitizedContent = validateAndSanitize(blueprintData.content);

      const { data: blueprint, error } = await supabase
        .from('project_blueprints')
        .insert({
          project_id: projectId,
          blueprint_type: blueprintData.type || 'requirements',
          version: blueprintData.version || 1,
          is_locked: blueprintData.is_locked || false,
          created_by: userId,
          content: sanitizedContent,
          metadata: {
            ...blueprintData.metadata,
            validation: metadata?.validation, // Store validation results
          },
        })
        .select()
        .single();

      if (error) {
        logger.error('Error storing blueprint', { error, projectId, userId });
        throw error;
      }

      logger.info('Blueprint stored successfully with validation', {
        blueprintId: blueprint.id,
        projectId,
        validationErrors: metadata?.validation?.errors?.length || 0,
      });

      return blueprint;
    } catch (error) {
      logger.error('Error in storeBlueprint with validation', { error, projectId, userId });
      throw error;
    }
  }

  /**
   * Get blueprint with validation results
   */
  static async getValidatedBlueprint(
    blueprintId: string,
    projectId: string,
    userId: string
  ): Promise<ValidatedBlueprint | null> {
    try {
      const { data: blueprint } = await supabase
        .from('project_blueprints')
        .select('*')
        .eq('id', blueprintId)
        .eq('project_id', projectId)
        .eq('created_by', userId)
        .single();

      if (!blueprint) return null;

      // Run validation on retrieved blueprint
      const validation = await validateBlueprintContent(blueprint.content);
      
      return {
        ...blueprint,
        validation,
      };
    } catch (error) {
      logger.error('Error getting validated blueprint', {
        error,
        blueprintId,
        projectId,
        userId,
      });
      throw error;
    }
  }
}
